// components/Bills.js

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faBinoculars, faGolfBallTee, faExclamationTriangle, faCalendarWeek, faScaleBalanced, faScaleUnbalanced, faScaleUnbalancedFlip, faInfoCircle, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { Table, Card, Row, Nav, Col, Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import BillSelectionTotal from './BillSelectionTotal';
import AddBillForm from '../Forms/AddBillForm';
import BillSearch from './BillSearch';
import {
  getBills,
  getTotalThisWeek,
  getTotalNextWeek,
  getTotalFortnight,
  getFortnightBillsData,
  getBillsForFortnight,
  getFutureMonthBills,
  updateBillPaidStatus,
  getFortnightBills,
  deleteBill
} from '../../services/BillService';
import { getBalance, updateBalance, getConsolidatedBankAccountData } from '../../services/BankAccountService';
import { determineCurrentWeek, formatDate, formatShortDateRange, formatReadableDate, getFortnightDates, parseDateOnly, isSameYMD, calculatePeriodDates } from '../../utils/dateUtils';
import {
  getPays,
  getPaysWithUnpaidTransfers,
  getPaysBankAccountsForFortnight,
  getConsolidatedPayData
} from '../../services/PayService';
import UpdateBalance from '../Balance';
import TotalCard from '../TotalCard';
import PeriodSwitcher from '../PeriodSwitcher';
import EquationCards from '../EquationCards';
import ShadowBoxTable from '../ShadowBoxTable';
import BalanceGraph from './balanceGraph.js';
import Bill from './Bill';
import PaidBillsGroup from './PaidBillsGroup';
import { getBankAccounts } from '../../services/BankAccountService';
import ErrorBoundary from '../ErrorBoundary.js';
import ChartContainer from './ChartContainer.js';
import BillsTable from './BillsTable';
import BillSelectionPortal from './BillSelectionPortal';
import BankAccountNavigation from './BankAccountNavigation';
import useIsPhone from '../../hooks/useIsPhone';
import PhoneBillsView from './mobile/PhoneBillsView';
import '../../css/Bills.css';
import { getOverdueBills } from '../../services/BillService';

// Debug utility functions - only log in development environment
const debugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

const debugError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(...args);
  }
};

function BillGroupLabel({ title, variant, tooltip, icon }) {
  const tooltipId = `bill-group-${title.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className={`bills-group-label bills-group-label--${variant}`}>
      {icon && <FontAwesomeIcon icon={icon} className="bills-group-label__icon" />}
      <span>{title}</span>
      {tooltip && (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}
        >
          <FontAwesomeIcon
            icon={faInfoCircle}
            className="bills-group-label__info"
            onClick={(e) => e.stopPropagation()}
          />
        </OverlayTrigger>
      )}
    </div>
  );
}

function CollapsibleBillGroup({ title, variant, tooltip, icon, defaultOpen = true, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const tooltipId = `bill-group-${title.replace(/\s+/g, '-').toLowerCase()}`;

  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <div className="bills-collapsible-group">
      <div
        className={`bills-group-label bills-group-label--${variant} bills-group-label--clickable`}
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        {icon && <FontAwesomeIcon icon={icon} className="bills-group-label__icon" />}
        <span>{title}</span>
        {tooltip && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}
          >
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="bills-group-label__info"
              onClick={(e) => e.stopPropagation()}
            />
          </OverlayTrigger>
        )}
        <FontAwesomeIcon
          icon={isOpen ? faChevronUp : faChevronDown}
          className="bills-group-label__chevron"
        />
      </div>
      {isOpen && <div className="bills-collapsible-group__content">{children}</div>}
    </div>
  );
}

const debugWarn = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(...args);
  }
};


const Bills = () => {
  const { bankAccountId: urlBankAccountId } = useParams();
  const [activeTab, setActiveTab] = useState("#fortnight");
  const [title, setTitle] = useState("first");
  const [graphData, setGraphData] = useState([]);
  const [graphDataWindow, setGraphDataWindow] = useState({
    start: new Date(),
    end: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000), // Reduced from 27 to 21 days
    data: []
  });
  const [selectedBills, setSelectedBills] = useState([]);
  const [lastSelected, setLastSelected] = useState(null);
  const [showAddBillModal, setShowAddBillModal] = useState(false);
  const [showAddPayModal, setShowAddPayModal] = useState(false);
  const [showAllBills, setShowAllBills] = useState(false);
  // Add a cache for pay data to avoid redundant fetching
  const [payCache, setPayCache] = useState({
    // Cache format: { 'start_date-end_date-bankAccountId': { data, timestamp } }
    currentWeek: null,
    nextWeek: null,
    fortnight: null,
    timestamp: 0
  });
  const [state, setState] = useState({
    bills: [],
    pays: [],
    filteredBills: [],
    balance: 0,
    totals: {
      totalThisWeek: 0,
      totalNextWeek: 0,
      totalFortnight: 0,
    },
    transferTotals: {
      totalUnpaidPaysThisWeek: 0,
      totalUnpaidPaysNextWeek: 0,
      totalUnpaidPaysThisFortnight: 0,
    },
    fortnightDates: {
      firstWeekStart: null,
      firstWeekEnd: null,
      secondWeekStart: null,
      secondWeekEnd: null,
      thirdWeekStart: null,
      thirdWeekEnd: null,
    },
    currentBalance: 0,
    bankAccountId: null,
    bankAccounts: [],
    currentWeekBills: [],
    nextWeekBills: [],
    prevUnpaidBills: [],
    currentWeekPaidBills: [],
    nextWeekPaidBills: [],
    // Overdue pagination state (for fortnight tab)
    overdueBills: [],
    overdueTotal: 0,
    overdueLimit: 20,
    overduePageApi: 1,
    overdueDisplayedPage: 1,
    overdueTotalPages: 0,
    overdueLoading: false,
    overdueContextKey: null,
  });

  const now = new Date();
  const currentDate = now.toISOString().split('T')[0]; // Use this for API call
  const [showUpdateBalance, setShowUpdateBalance] = useState(false);
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [trackingPeriod, setTrackingPeriod] = useState('current-fortnight');

  const { bankAccountId } = useParams();

  // Add pagination state variables
  const [futureBillsPage, setFutureBillsPage] = useState(1);
  const [futureBillsHasMore, setFutureBillsHasMore] = useState(false);
  const [futureBillsTotal, setFutureBillsTotal] = useState(0);
  
  const [recentBillsPage, setRecentBillsPage] = useState(1);
  const [recentBillsHasMore, setRecentBillsHasMore] = useState(false);
  const [recentBillsTotal, setRecentBillsTotal] = useState(0);
  
  const BILLS_PER_PAGE = 50;

  const isPhone = useIsPhone();

  // Guard to prevent overlapping fetches and redundant refetches
  const isFetchingRef = useRef(false);
  const [consolidatedPaysDisabled, setConsolidatedPaysDisabled] = useState(false);
  // TTL for reusing consolidated pays payload without re-fetching
  const CONSOLIDATED_PAYS_TTL_MS = 60 * 1000; // 60 seconds
  // Sequence token to ensure only the latest fetch commits state
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    fetchBalance();
  }, [state.bankAccountId, showAllBills]);

  const fetchBalance = async () => {
    try {
      // Optimize: Use existing bankAccounts from state instead of calling API
      if (state.bankAccountId && !showAllBills && state.bankAccounts.length > 0) {
        // Use the already-fetched bankAccounts from state - no API call needed!
        const bankAccount = state.bankAccounts.find(acc => acc.id === state.bankAccountId);
        
        if (bankAccount) {
          setState((prevState) => ({
            ...prevState,
            currentBalance: parseFloat(bankAccount.balance || 0),
          }));
          return; // Exit early - no API call needed
        }
      }
      
      // Only make API calls if we don't have the data in state
      if (state.bankAccountId && !showAllBills) {
        // Fallback to API call only if bank account not found in state
        const balanceData = await getBalance(state.bankAccountId);
        setState((prevState) => ({
          ...prevState,
          currentBalance: parseFloat(balanceData?.amount || 0),
        }));
      } else if (!showAllBills) {
        // For non-specific account, use the tracking account balance
        const { getTrackingAccountBalance } = await import('../../services/BankAccountService');
        const balanceData = await getTrackingAccountBalance();
        setState((prevState) => ({
          ...prevState,
          currentBalance: parseFloat(balanceData?.amount || 0),
        }));
      } else {
        // For all bills view, set balance to 0
        setState((prevState) => ({
          ...prevState,
          currentBalance: 0,
        }));
      }
    } catch (error) {
      debugError('Error fetching balance:', error.message);
      debugError('Error fetching balance (full details):', error);
      setState((prevState) => ({
        ...prevState,
        currentBalance: 0,
      }));
    }
  };

  const handleAddBillClick = () => setShowAddBillModal(true);
  const handleAddPayClick = () => setShowAddPayModal(true);

  const handleBalanceUpdate = (newBalanceAmount) => {
    // Update the balance immediately in state without refetching
    const newBalance = parseFloat(newBalanceAmount || 0);
    
    // Capture current state values before updating
    const currentBills = state.bills;
    const currentPays = state.pays;
    const currentOverdueBills = state.overdueBills;
    const currentBankAccountId = state.bankAccountId;
    
    setState((prevState) => {
      // Update the bankAccounts array with the new balance
      const updatedBankAccounts = prevState.bankAccounts.map(acc => 
        acc.id === currentBankAccountId 
          ? { ...acc, balance: newBalanceAmount }
          : acc
      );
      
      return {
        ...prevState,
        currentBalance: newBalance,
        bankAccounts: updatedBankAccounts
      };
    });
    
    // Regenerate graph data with the new balance
    const newGraphData = generateGraphData(
      currentBills,
      currentPays,
      currentOverdueBills,
      newBalance,
      graphDataWindow.start,
      graphDataWindow.end
    );
    
    if (Array.isArray(newGraphData) && newGraphData.length > 0) {
      setGraphData(newGraphData);
      setGraphDataWindow(prev => ({
        ...prev,
        data: newGraphData
      }));
    }
    
    setShowUpdateBalance(false);
  };

  const handleDelete = async (billId, seriesId) => {
  try {
    // Call the API to delete the bill
    await deleteBill(billId, !!seriesId);
    
    // Update local state to reflect the deletion
    setState(prevState => {
      if (seriesId) {
        // For series deletion: remove all bills with matching series ID and due date >= current bill
        const currentBill = prevState.bills.find(b => b.id === billId);
        if (!currentBill) return prevState; // Safety check
        
        return {
          ...prevState,
          bills: prevState.bills.filter(bill =>
            !(bill.bill_series_id === seriesId && bill.due_date >= currentBill.due_date)
          ),
          currentWeekBills: prevState.currentWeekBills.filter(bill => 
            !(bill.bill_series_id === seriesId && bill.due_date >= currentBill.due_date)
          ),
          nextWeekBills: prevState.nextWeekBills.filter(bill => 
            !(bill.bill_series_id === seriesId && bill.due_date >= currentBill.due_date)
          ),
          filteredBills: prevState.filteredBills.filter(bill => 
            !(bill.bill_series_id === seriesId && bill.due_date >= currentBill.due_date)
          ),
          currentWeekPaidBills: prevState.currentWeekPaidBills.filter(bill => 
            !(bill.bill_series_id === seriesId && bill.due_date >= currentBill.due_date)
          ),
          nextWeekPaidBills: prevState.nextWeekPaidBills.filter(bill => 
            !(bill.bill_series_id === seriesId && bill.due_date >= currentBill.due_date)
          )
        };
      } else {
        // For single bill deletion: just remove that specific bill
        return {
          ...prevState,
          bills: prevState.bills.filter(bill => bill.id !== billId),
          currentWeekBills: prevState.currentWeekBills.filter(bill => bill.id !== billId),
          nextWeekBills: prevState.nextWeekBills.filter(bill => bill.id !== billId),
          filteredBills: prevState.filteredBills.filter(bill => bill.id !== billId),
          currentWeekPaidBills: prevState.currentWeekPaidBills.filter(bill => bill.id !== billId),
          nextWeekPaidBills: prevState.nextWeekPaidBills.filter(bill => bill.id !== billId)
        };
      }
    });
    
    // Refresh the totals after deletion
    await fetchInitialData();
  } catch (error) {
    debugError('Error deleting bill:', error.message);
    debugError('Error deleting bill (full details):', error);
    // You might want to show an error message to the user here
  }
};

// Modify the handleBillSelect function:
const handleBillSelect = (bill, event) => {
  // Don't select if clicking a button or the notes icon
  if (
    event.target.tagName === 'BUTTON' ||
    event.target.closest('button') ||
    event.target.closest('svg[data-icon="sticky-note"]') ||
    event.target.closest('svg[data-icon="add-note"]')
  ) {    return;
  }

  // Don't select if it's a pay row
  if (bill.description === "incoming pay") {
    return;
  }

  if (event.shiftKey && lastSelected) {
    // Get the relevant bill list based on which section we're in
    // Include filteredBills for other tabs
    const billList = activeTab === "#fortnight"
      ? state.currentWeekBills.concat(state.nextWeekBills)
      : state.filteredBills;
    const start = billList.findIndex(b => b.id === lastSelected.id);
    const end = billList.findIndex(b => b.id === bill.id);

    // Get all bills between start and end indexes
    const range = billList.slice(
      Math.min(start, end),
      Math.max(start, end) + 1
    );

    setSelectedBills(prev => {
      const newSelection = new Set(prev);
      range.forEach(b => {
        if (!b.description || b.description !== "incoming pay") {
          newSelection.add(b);
        }
      });
      return Array.from(newSelection);
    });
  } else {
    setSelectedBills(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(bill)) {
        newSelection.delete(bill);
      } else {
        newSelection.add(bill);
      }
      return Array.from(newSelection);
    });
    setLastSelected(bill);
  }
};

  // Add a function to check if the cache is still valid (e.g., not older than 5 minutes)
  const isCacheValid = (cacheKey) => {
    if (!payCache[cacheKey]) return false;
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
    return (Date.now() - payCache.timestamp) < CACHE_TTL;
  };

  // Function to get pays with caching
  const getCachedPaysWithUnpaidTransfers = async (start, end, bankAccountId, cacheKey) => {
    if (!bankAccountId) return { pays: [], totalUnpaidTransferAmount: 0 };
    
    // Generate cache key from date range and bank account
    if (isCacheValid(cacheKey)) {
      return payCache[cacheKey];
    }
    
    // If not in cache or expired, fetch from API
    const result = await getPaysWithUnpaidTransfers(start, end, bankAccountId);
    
    // Update cache
    setPayCache(prev => ({
      ...prev,
      [cacheKey]: result,
      timestamp: Date.now()
    }));
    
    return result;
  };

  // Move fetchInitialData out here so it can be referenced elsewhere
  const fetchInitialData = async () => {
    // Start a new fetch cycle and capture its sequence number
    isFetchingRef.current = true;
    const mySeq = ++fetchSeqRef.current;
    setLoading(true);
 
    try {
      // Fetch balance, bills, fortnight dates, and bank accounts
      let balanceAmount = { amount: 0 };
      let dates = {
        firstWeekStart: new Date(),
        firstWeekEnd: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000),
        secondWeekStart: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        secondWeekEnd: new Date(Date.now() + 13 * 24 * 60 * 60 * 1000),
        thirdWeekStart: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        thirdWeekEnd: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000),
      };
      let bankAccounts = [];
      let allBills = [];
      let mappedPays = [];
      let currentWeekBills = [];
      let nextWeekBills = [];
      let filteredBills = [];
      let currentWeekPaidBills = [];
      let nextWeekPaidBills = [];
      let totalUnpaidCurrentWeek = 0;
      let totalUnpaidNextWeek = 0;
      let mappedCurrentWeekPays = [];
      let mappedNextWeekPays = [];
      let mappedFuturePays = [];
      // Overdue pagination locals in outer scope so we can use them later
      let overdueBillsLocal = [];
      let overdueTotalLocal = 0;
      let overdueTotalPagesLocal = 1;
 
      // Get tracked bank account ID or the one from URL params
      // For "All Bills" view (no urlBankAccountId), always use null regardless of tracked accounts
      // Use URL directly instead of potentially stale showAllBills state
      const selectedBankAccountId = urlBankAccountId ? parseInt(urlBankAccountId, 10) : null;

      // Fire off consolidated promises early to overlap network time
      let consolidatedPaysPromise = null;
      let consolidatedBankPromise = null;
      if (selectedBankAccountId && !consolidatedPaysDisabled) {
        consolidatedPaysPromise = getConsolidatedPayData(
          selectedBankAccountId,
          'fortnight_plus_60days',
          0
        );
        consolidatedBankPromise = getConsolidatedBankAccountData(selectedBankAccountId, 0);
      }

      // Always fetch ALL bank accounts first for navigation
      try {
        bankAccounts = await getBankAccounts();
      } catch (accountsError) {
        debugWarn("Error fetching bank accounts:", accountsError.message);
        debugWarn("Error fetching bank accounts (full details):", accountsError);
      }

      // 🚀 OPTIMIZATION: For specific bank accounts, use consolidated endpoint for balance
      if (selectedBankAccountId) {
        try {
          debugLog('🚀 Using consolidated bank account data endpoint');
          const consolidatedBankData = consolidatedBankPromise
            ? await consolidatedBankPromise
            : await getConsolidatedBankAccountData(selectedBankAccountId, 0);
          const bankAccountData = consolidatedBankData.bank_account;
          balanceAmount = { amount: bankAccountData.balance };
          debugLog('🚀 Consolidated bank account data loaded:', {
            nickname: bankAccountData.nickname,
            balance: bankAccountData.balance,
            requirementsCount: consolidatedBankData.requirements.count
          });
        } catch (consolidatedError) {
          debugWarn('🔄 Consolidated bank account endpoint failed, falling back to individual calls:', consolidatedError.message);
          
          // FALLBACK: Use individual API calls for balance
          try {
            if (selectedBankAccountId) {
              const selectedBankAccount = bankAccounts.find(acc => acc.id === selectedBankAccountId);
              if (selectedBankAccount) {
                balanceAmount = { amount: selectedBankAccount.balance || "0.00" };
              } else {
                balanceAmount = await getBalance(selectedBankAccountId);
              }
            } else {
              balanceAmount = await getBalance();
            }
          } catch (balanceError) {
            debugWarn("Error getting balance:", balanceError.message);
            debugWarn("Error getting balance (full details):", balanceError);
            balanceAmount = { amount: "0.00" };
          }
        }
      } else {
        // For "All Bills" view, get default balance
        try {
          balanceAmount = await getBalance();
        } catch (balanceError) {
          debugWarn("Error getting balance:", balanceError.message);
          debugWarn("Error getting balance (full details):", balanceError);
          balanceAmount = { amount: "0.00" };
        }
      }

      try {
        // Try to fetch fortnight dates
        dates = await getFortnightDates();
      } catch (datesError) {
        debugWarn("Error fetching fortnight dates:", datesError.message);
        debugWarn("Error fetching fortnight dates (full details):", datesError);
      }

      try {
        // Fetch only the bills needed for the fortnight view using a smart date filter
        // Start 2 weeks before the current week start to include overdue bills as well
        const initialCurrentWeekStart =
          determineCurrentWeek(dates) === "first"
            ? new Date(dates.firstWeekStart)
            : new Date(dates.secondWeekStart);

        // Include two weeks prior to cover near-overdue while relying on overdue fetch for deeper history
        const startDate = new Date(initialCurrentWeekStart);
        startDate.setDate(startDate.getDate() - 14);
        const filterDate = startDate.toISOString().split("T")[0];

        // Include bankAccountId for server-side filtering when viewing a specific account
        const billsResponse = await getBills(
          filterDate,                 // due_date__gte
          undefined,                  // isPaid
          'due_date',                 // sortField
          'asc',                      // sortOrder
          selectedBankAccountId || null, // bank_account
          1,                          // page
          200                         // limit
        );
        
        // Extract the bills array from the response object
        allBills = billsResponse.bills || billsResponse;

        // Ensure allBills is an array before filtering
        if (!Array.isArray(allBills)) {
          debugError('Error: getBills did not return valid bills data', billsResponse);
          allBills = []; // Initialize as empty array if not valid
        } 
        
        debugLog('📊 Bills data loaded:', {
          totalBills: allBills.length,
          selectedBankAccountId: selectedBankAccountId,
          showAllBills: showAllBills,
          filterDate,
        });
        
        // Debug: Log some sample bills
        if (allBills.length > 0) {
          debugLog('📊 Sample bills (first 3):', allBills.slice(0, 3).map(bill => ({
            id: bill.id,
            name: bill.name,
            amount: bill.amount,
            due_date: bill.due_date,
            paid: bill.paid,
            bank_account: bill.bank_account
          })));
        }
       
        // Filter by bank account if needed
        const filteredAllBills = !showAllBills && selectedBankAccountId ? 
          allBills.filter(bill => bill.bank_account === selectedBankAccountId) : 
          allBills;
          
        debugLog('🔍 After bank account filtering:', {
          originalCount: allBills.length,
          filteredCount: filteredAllBills.length,
          filterCriteria: selectedBankAccountId ? `bank_account === ${selectedBankAccountId}` : 'no filter'
        });
        
        // Calculate the week start and end dates based on fortnight dates
        const currentWeekStart = determineCurrentWeek(dates) === "first" ? new Date(dates.firstWeekStart) : new Date(dates.secondWeekStart);
        const currentWeekEnd = determineCurrentWeek(dates) === "first" ? new Date(dates.firstWeekEnd) : new Date(dates.secondWeekEnd);
        const nextWeekStart = determineCurrentWeek(dates) === "first" ? new Date(dates.secondWeekStart) : new Date(dates.thirdWeekStart);
        const nextWeekEnd = determineCurrentWeek(dates) === "first" ? new Date(dates.secondWeekEnd) : new Date(dates.thirdWeekEnd);
        
        debugLog('📅 Week date ranges for filtering:', {
          currentWeekStart: currentWeekStart.toISOString().split('T')[0],
          currentWeekEnd: currentWeekEnd.toISOString().split('T')[0],
          nextWeekStart: nextWeekStart.toISOString().split('T')[0],
          nextWeekEnd: nextWeekEnd.toISOString().split('T')[0]
        });
        
        // Get bills due in current and next week
        let current_week_bills = filteredAllBills.filter(bill => {
          const billDate = parseDateOnly(bill.due_date);
          const weekStart = parseDateOnly(currentWeekStart);
          const weekEnd = parseDateOnly(currentWeekEnd);
          return billDate >= weekStart && billDate <= weekEnd;
        });
          
        let next_week_bills = filteredAllBills.filter(bill => {
          const billDate = parseDateOnly(bill.due_date);
          const weekStart = parseDateOnly(nextWeekStart);
          const weekEnd = parseDateOnly(nextWeekEnd);
          return billDate >= weekStart && billDate <= weekEnd;
        });
        
        // Fetch top overdue unpaid bills (most recent first) limited to state.overdueLimit
        const overdueCutoff = currentWeekStart.toISOString().split('T')[0];
        const overdueResp = await getOverdueBills(
          overdueCutoff,
          selectedBankAccountId || null,
          1,
          state.overdueLimit || 20
        );
        overdueTotalLocal = overdueResp.total || 0;
        overdueTotalPagesLocal = Math.max(1, Math.ceil(overdueTotalLocal / (state.overdueLimit || 20)));
        // Default to API page 1 (most recent) and display page 1 for consistency
        overdueBillsLocal = Array.isArray(overdueResp.bills) ? overdueResp.bills : [];
        
        debugLog('📊 Bills by week after date filtering:', {
          currentWeekBills: current_week_bills.length,
          nextWeekBills: next_week_bills.length,
          prevUnpaidBills: overdueBillsLocal.length
        });
        
        // Debug: Log the actual bills found
        if (current_week_bills.length > 0) {
          debugLog('📊 Current week bills:', current_week_bills.map(bill => ({
            id: bill.id,
            name: bill.name,
            due_date: bill.due_date,
            paid: bill.paid,
            amount: bill.amount
          })));
        }
        
        if (next_week_bills.length > 0) {
          debugLog('📊 Next week bills:', next_week_bills.map(bill => ({
            id: bill.id,
            name: bill.name,
            due_date: bill.due_date,
            paid: bill.paid,
            amount: bill.amount
          })));
        }
        
        // Improved filter for bills paid in current week - this should include ALL bills paid during
        // the current week regardless of their due date
        let bills_paid_in_current_week = filteredAllBills.filter(bill => {
          // Check if the bill is paid and the paid date is within the current week
          return bill.paid && bill.paid_date && 
            new Date(bill.paid_date) >= currentWeekStart && 
            new Date(bill.paid_date) < nextWeekStart;
        });

        // Separate paid and unpaid bills for current and next week
        let currentWeekUnpaidBills = current_week_bills.filter(bill => !bill.paid);
        // Use bills_paid_in_current_week directly
        currentWeekPaidBills = bills_paid_in_current_week;
        let nextWeekUnpaidBills = next_week_bills.filter(bill => !bill.paid);
        nextWeekPaidBills = next_week_bills.filter(bill => bill.paid);
        
        // Only fetch pays if we're showing filtered bills
          if (selectedBankAccountId) {
          // 🚀 NEW OPTIMIZED APPROACH: Use consolidated endpoint to eliminate N+1 queries
          try {
            if (!consolidatedPaysDisabled) {
              // Reuse cached consolidated data if fresh and for the same bank account
              const nowTs = Date.now();
              const hasFreshCache = !!(payCache.consolidatedData
                && payCache.consolidatedData.bank_account
                && payCache.consolidatedData.bank_account.id === selectedBankAccountId
                && (nowTs - (payCache.timestamp || 0) < CONSOLIDATED_PAYS_TTL_MS));

              const consolidatedData = hasFreshCache
                ? payCache.consolidatedData
                : (consolidatedPaysPromise
                    ? await consolidatedPaysPromise
                    : await getConsolidatedPayData(
              selectedBankAccountId,
              'fortnight_plus_60days',
                        0
                      ));
            totalUnpaidCurrentWeek = consolidatedData.totals.unpaid_current_week;
            totalUnpaidNextWeek = consolidatedData.totals.unpaid_next_week;
            debugLog('🚀 Consolidated data loaded:', {
              currentWeekPays: consolidatedData.counts.current_week,
              nextWeekPays: consolidatedData.counts.next_week,
              futurePays: consolidatedData.counts.future,
              totalPays: consolidatedData.counts.total,
              unpaidCurrentWeek: totalUnpaidCurrentWeek,
              unpaidNextWeek: totalUnpaidNextWeek,
              dateRanges: consolidatedData.date_ranges,
              debugInfo: consolidatedData.metadata?.debug_info
            });
            debugLog('🚀 Raw consolidated pays data:', {
              current_week_pays: consolidatedData.pays.current_week,
              next_week_pays: consolidatedData.pays.next_week,
                 future_pays: consolidatedData.pays.future.slice(0, 3)
               });
               debugLog('🔍 DEBUG TRANSFERS - Raw consolidated data sample:', {
                 totalPays: consolidatedData.pays.current_week.length + consolidatedData.pays.next_week.length + consolidatedData.pays.future.length,
                 currentWeekSample: consolidatedData.pays.current_week[0] ? {
                   id: consolidatedData.pays.current_week[0].id,
                   nickname: consolidatedData.pays.current_week[0].nickname,
                   next_pay: consolidatedData.pays.current_week[0].next_pay,
                   bank_account_details: consolidatedData.pays.current_week[0].bank_account_details
                 } : 'none',
                 nextWeekSample: consolidatedData.pays.next_week[0] ? {
                   id: consolidatedData.pays.next_week[0].id,
                   nickname: consolidatedData.pays.next_week[0].nickname,
                   next_pay: consolidatedData.pays.next_week[0].next_pay,
                   bank_account_details: consolidatedData.pays.next_week[0].bank_account_details
                 } : 'none'
                              });
               const mapPayData = (payData) => ({
                 id: payData.id,
                 name: payData.nickname,
                 description: "incoming pay",
                 amount: payData.bank_account_details.transfer_amount,
                 due_date: payData.next_pay,
                 paid: payData.bank_account_details.transfer_made,
                 paid_date: payData.bank_account_details.transfer_date,
                 transferAmount: payData.bank_account_details.transfer_amount,
                 transferMade: payData.bank_account_details.transfer_made,
                 pay_series_id: payData.pay_series_id,
               });
               // Assign to outer scope variables (not local ones)
               mappedCurrentWeekPays = consolidatedData.pays.current_week
                 .filter(pay => pay.bank_account_details.transfer_amount !== '0.00')
                 .map(mapPayData);
               mappedNextWeekPays = consolidatedData.pays.next_week
                 .filter(pay => pay.bank_account_details.transfer_amount !== '0.00')
                 .map(mapPayData);
               mappedFuturePays = consolidatedData.pays.future
                 .filter(pay => pay.bank_account_details.transfer_amount !== '0.00')
                 .map(mapPayData);
            mappedPays = [...mappedCurrentWeekPays, ...mappedNextWeekPays, ...mappedFuturePays];
               debugLog('🔍 DEBUG TRANSFERS - Consolidated pays mapped:', {
                 totalMappedPays: mappedPays.length,
                 currentWeek: mappedCurrentWeekPays.length,
                 nextWeek: mappedNextWeekPays.length,
                 future: mappedFuturePays.length,
                 allPaysDates: mappedPays.map(p => ({ id: p.id, nickname: p.nickname, nextPay: p.next_pay, transferAmount: p.transferAmount }))
               });
               // Only update the cache timestamp if we fetched new data; keep existing timestamp for reused cache
               const nextTimestamp = hasFreshCache ? (payCache.timestamp || Date.now()) : Date.now();
            setPayCache(prev => ({
              ...prev,
              consolidatedData: consolidatedData,
              currentWeek: { pays: mappedCurrentWeekPays, totalUnpaidTransferAmount: totalUnpaidCurrentWeek },
              nextWeek: { pays: mappedNextWeekPays, totalUnpaidTransferAmount: totalUnpaidNextWeek },
              futurePays: { pays: mappedFuturePays, totalUnpaidTransferAmount: consolidatedData.totals.unpaid_future },
              allPays: mappedPays,
                 timestamp: nextTimestamp
            }));
            currentWeekBills = [...currentWeekUnpaidBills, ...mappedCurrentWeekPays].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
            nextWeekBills = [...nextWeekUnpaidBills, ...mappedNextWeekPays].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
            debugLog('🎯 Final combined data (with consolidated pays):', {
              currentWeekBills: currentWeekBills.length,
              nextWeekBills: nextWeekBills.length,
              currentWeekBreakdown: {
                unpaidBills: currentWeekUnpaidBills.length,
                priorUnpaidBills: overdueBillsLocal.length,
                pays: mappedCurrentWeekPays.length
              },
              nextWeekBreakdown: {
                unpaidBills: nextWeekUnpaidBills.length,
                pays: mappedNextWeekPays.length
              }
            });
            debugLog('🚀 Performance improvement: Single API call replaced ~12+ calls');
             } else {
               throw new Error('Consolidated pays endpoint disabled for this session');
             }
          } catch (consolidatedError) {
            debugWarn('🔄 Consolidated endpoint failed, falling back to legacy approach:', consolidatedError.message);
             setConsolidatedPaysDisabled(true);
            
            // FALLBACK: Use the original approach if consolidated endpoint fails
          const extendedEndDate = new Date(dates.secondWeekEnd);
            extendedEndDate.setDate(extendedEndDate.getDate() + 60);
          
          const fortnightResult = await getPaysBankAccountsForFortnight(
            new Date(dates.firstWeekStart),
            extendedEndDate,
            selectedBankAccountId
          );
          
          const { 
            pays: allFortnightPays, 
            firstWeekPays, 
            secondWeekPays,
            totalUnpaidFirstWeek,
            totalUnpaidSecondWeek,
              futurePays,
            totalUnpaidFuture
          } = fortnightResult;
          
          totalUnpaidCurrentWeek = totalUnpaidFirstWeek;
          totalUnpaidNextWeek = totalUnpaidSecondWeek;
          
            // Legacy mapping logic
          let mappedFortnightPays = allFortnightPays.map(pay => {
            const trackedAccount = pay.bank_accounts.find(ba => ba.bank_account === selectedBankAccountId);
            if (trackedAccount && trackedAccount.transfer_amount !== '0.00') {
              return {
                id: pay.id,
                name: pay.nickname,
                description: "incoming pay",
                amount: trackedAccount.transfer_amount,
                due_date: pay.next_pay,
                paid: trackedAccount.transfer_made,
                paid_date: trackedAccount.transfer_date || null,
                transferAmount: trackedAccount.transfer_amount,
                transferMade: trackedAccount.transfer_made,
              };
            }
              return null;
          }).filter(pay => pay !== null);
          
            mappedPays = mappedFortnightPays;
            
          const mappedCurrentWeekPays = firstWeekPays
            .map(pay => {
              const trackedAccount = pay.bank_accounts.find(ba => ba.bank_account === selectedBankAccountId);
              return trackedAccount && trackedAccount.transfer_amount !== '0.00'
                ? {
                    id: pay.id,
                    name: pay.nickname,
                    description: "incoming pay",
                    amount: trackedAccount.transfer_amount,
                    due_date: pay.next_pay,
                    paid: trackedAccount.transfer_made,
                    paid_date: trackedAccount.transfer_date || null,
                    transferAmount: trackedAccount.transfer_amount,
                    transferMade: trackedAccount.transfer_made,
                  }
                : null;
            })
            .filter(pay => pay !== null);

          const mappedNextWeekPays = secondWeekPays
            .filter(pay => {
              const payDate = new Date(pay.next_pay);
              return payDate >= nextWeekStart && payDate <= nextWeekEnd;
            })
            .map(pay => {
              const trackedAccount = pay.bank_accounts.find(ba => ba.bank_account === selectedBankAccountId);
              return trackedAccount && trackedAccount.transfer_amount !== '0.00'
                ? {
                    id: pay.id,
                    name: pay.nickname,
                    description: "incoming pay",
                    amount: trackedAccount.transfer_amount,
                    due_date: pay.next_pay,
                    paid: trackedAccount.transfer_made,
                    paid_date: trackedAccount.transfer_date || null,
                    transferAmount: trackedAccount.transfer_amount,
                    transferMade: trackedAccount.transfer_made,
                  }
                : null;
            })
            .filter(pay => pay !== null);

                      currentWeekBills = [...currentWeekUnpaidBills, ...mappedCurrentWeekPays].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
            nextWeekBills = [...nextWeekUnpaidBills, ...mappedNextWeekPays].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
            
            debugLog('🔄 Final combined data (legacy fallback):', {
              currentWeekBills: currentWeekBills.length,
              nextWeekBills: nextWeekBills.length,
              currentWeekBreakdown: {
                unpaidBills: currentWeekUnpaidBills.length,
                priorUnpaidBills: 0,
                pays: mappedCurrentWeekPays.length
              },
              nextWeekBreakdown: {
                unpaidBills: nextWeekUnpaidBills.length,
                pays: mappedNextWeekPays.length
              }
            });
          }
        } else {
          // Just use the bills without pays when showing all bills; overdue shown separately
          currentWeekBills = [...currentWeekUnpaidBills].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
          nextWeekBills = [...nextWeekUnpaidBills].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
          
          debugLog('📄 All Bills view (no pays included):', {
            currentWeekBills: currentWeekBills.length,
            nextWeekBills: nextWeekBills.length,
            currentWeekBreakdown: {
              unpaidBills: currentWeekUnpaidBills.length,
              priorUnpaidBills: overdueBillsLocal.length
            },
            nextWeekBreakdown: {
              unpaidBills: nextWeekUnpaidBills.length
            }
          });
        }

        // Set filtered bills based on title
        filteredBills = title === "first" ? currentWeekBills : nextWeekBills;

      } catch (billsError) {
        debugWarn("Error fetching bills:", billsError.message);
        debugWarn("Error fetching bills (full details):", billsError);
        allBills = []; // Ensure allBills is an array even if fetch fails
      }

      // Combine all pays into a single array for use in the graph
      // This ensures we have all pays (current, next week and future) available
      // without needing extra API calls
      const allMappedPays = [];
      
      // Add all transfer arrays that exist
      if (mappedCurrentWeekPays && mappedCurrentWeekPays.length > 0) {
        allMappedPays.push(...mappedCurrentWeekPays);
      }
      if (mappedNextWeekPays && mappedNextWeekPays.length > 0) {
        allMappedPays.push(...mappedNextWeekPays);
      }
      if (mappedFuturePays && mappedFuturePays.length > 0) {
        allMappedPays.push(...mappedFuturePays);
      }
      
      debugLog('🔍 DEBUG TRANSFERS - Building allMappedPays:', {
        currentWeekCount: mappedCurrentWeekPays?.length || 0,
        nextWeekCount: mappedNextWeekPays?.length || 0,
        futureCount: mappedFuturePays?.length || 0,
        totalAllMappedPays: allMappedPays.length,
        samplePay: allMappedPays[0] ? {
          id: allMappedPays[0].id,
          nickname: allMappedPays[0].nickname,
          dueDate: allMappedPays[0].due_date,
          transferAmount: allMappedPays[0].transferAmount
        } : 'none'
      });
      
      // Save the combined array to state, but only if this fetch is still the latest
      if (fetchSeqRef.current !== mySeq) {
        return; // stale fetch; do not commit
      }
      debugLog('🔍 DEBUG TRANSFERS - About to setState with pays:', {
        totalPays: allMappedPays.length,
        paysWithDates: allMappedPays.map(p => ({ id: p.id, nickname: p.nickname, nextPay: p.next_pay, transferAmount: p.transferAmount }))
      });
      setState(prevState => {
        // Compute a context key for overdue tied to the current fortnight and selected account
        const contextCutoff = (determineCurrentWeek(dates) === 'first'
          ? new Date(dates.firstWeekStart)
          : new Date(dates.secondWeekStart)
        ).toISOString().split('T')[0];
        const newState = {
          ...prevState,
          balance: parseFloat(balanceAmount.amount || 0),
          fortnightDates: dates,
          bankAccountId: selectedBankAccountId,
          bankAccounts: bankAccounts,
          currentBalance: parseFloat(balanceAmount.amount || 0),
          bills: allBills,
          pays: allMappedPays, // Use the combined array with all pays
          currentWeekBills: currentWeekBills || [],
          nextWeekBills: nextWeekBills || [],
          filteredBills: filteredBills || [],
          currentWeekPaidBills: currentWeekPaidBills || [],
          nextWeekPaidBills: nextWeekPaidBills || [],
          transferTotals: {
            totalUnpaidPaysThisWeek: totalUnpaidCurrentWeek,
            totalUnpaidPaysNextWeek: totalUnpaidNextWeek,
            totalUnpaidPaysThisFortnight: totalUnpaidCurrentWeek + totalUnpaidNextWeek,
          },
          // Overdue pagination fields
          overdueBills: overdueBillsLocal,
          overdueTotal: overdueTotalLocal,
          overduePageApi: 1,
          overdueDisplayedPage: 1,
          overdueTotalPages: overdueTotalPagesLocal,
          overdueLoading: false,
          overdueContextKey: `${contextCutoff}|${selectedBankAccountId || ''}`,
        };
        
        // Generate graph data even if there are no bills for this account
        try {
          const newGraphData = generateGraphData(
            newState.bills,
            newState.pays,
            newState.overdueBills,
            newState.currentBalance,
            graphDataWindow.start,
            graphDataWindow.end
          );

          // Only update graphData if it's not empty
          if (Array.isArray(newGraphData) && newGraphData.length > 0) {
            setGraphData(newGraphData);
          } else {
            debugWarn("Generated graph data is empty or invalid");
          }
        } catch (graphError) {
          debugWarn("Error generating graph data:", graphError.message);
          debugWarn("Error generating graph data (full details):", graphError);
        }
        
        return newState;
      });
    } catch (error) {
      debugError('Error fetching initial data:', error.message);
      debugError('Error fetching initial data (full details):', error);
      // Still set minimal state to avoid UI crashes
      if (fetchSeqRef.current !== mySeq) {
        return; // stale
      }
      setState(prevState => ({
        ...prevState,
        balance: 0,
        fortnightDates: {
          firstWeekStart: new Date(),
          firstWeekEnd: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000),
          secondWeekStart: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          secondWeekEnd: new Date(Date.now() + 13 * 24 * 60 * 60 * 1000),
          thirdWeekStart: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
          thirdWeekEnd: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000),
        },
        currentBalance: 0,
        bills: [],
        pays: []
      }));
    } finally {
      if (fetchSeqRef.current === mySeq) {
      setLoading(false);
        isFetchingRef.current = false;
      }
    }
  };

  // Effect to update showAllBills when urlBankAccountId changes
  useEffect(() => {
    setShowAllBills(!urlBankAccountId);
  }, [urlBankAccountId]);

  // Single consolidated useEffect for fetchInitialData to prevent race conditions
  useEffect(() => {
    // Always fetch initial data on component mount or when URL changes
    fetchInitialData();
  }, [urlBankAccountId]);

  // Separate effect only for fortnight tab navigation edge case
  useEffect(() => {
    // If fortnight tab shows no data (for specific account or All Bills), refetch
    if (
      activeTab === "#fortnight" &&
      state.currentWeekBills.length === 0 &&
      state.nextWeekBills.length === 0
    ) {
      if (!loading && !isFetchingRef.current) {
      fetchInitialData();
      }
    }
  }, [activeTab]);

  // When navigating back to the fortnight tab or switching bank accounts, re-enable consolidated endpoint
  useEffect(() => {
    if (activeTab === "#fortnight") {
      setConsolidatedPaysDisabled(false);
    }
  }, [activeTab, urlBankAccountId]);

  const calculateCurrentWeekData = () => {
    if (!state.bills || state.bills.length === 0) return [];
  
    const startDate = new Date(state.fortnightDates.firstWeekStart);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
  
    let currentBalance = state.currentBalance;

    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(startDate);
      date.setDate(date.getDate() + index);
      const dailyBills = state.bills.filter(bill => new Date(bill.due_date).toDateString() === date.toDateString());
  
      const income = dailyBills.filter(bill => bill.amount > 0).reduce((sum, bill) => sum + parseFloat(bill.amount), 0);
      const expenses = dailyBills.filter(bill => bill.amount < 0).reduce((sum, bill) => sum + Math.abs(parseFloat(bill.amount)), 0);
  
      const startBalance = currentBalance;
      currentBalance += income - expenses;

      return {
        date,
        startBalance,
        income,
        expenses,
        endBalance: currentBalance
      };
    });
  };

  // Function to generate graph data
  const generateGraphData = (bills, pays, overdueBills, initialBalance, startDate, endDate) => {
    // Handle case when bills array is empty or not provided
    if (!bills) {
      bills = [];
    }

    // Helpers for date-only comparison to avoid TZ issues
    const startYMD = parseDateOnly(startDate);
    const endYMD = parseDateOnly(endDate);

    // Merge in overdue bills so prior unpaid are fully represented on day 1
    let combinedBills = Array.isArray(bills) ? [...bills] : [];
    const overdueSource = Array.isArray(overdueBills) ? overdueBills : state.overdueBills;
    if (Array.isArray(overdueSource) && overdueSource.length > 0) {
      // Only include unpaid overdue items
      const overdueUnpaid = overdueSource.filter(b => !b.paid);
      // De-duplicate by id
      const seen = new Set(combinedBills.map(b => b.id));
      overdueUnpaid.forEach(b => { if (!seen.has(b.id)) combinedBills.push(b); });
    }

    // Use provided arrays directly; upstream already filters by account/context
    const filteredBills = combinedBills;

    // Transfers list: use provided pays; default to empty
    let filteredPays = Array.isArray(pays) ? [...pays] : [];
    debugLog('🔍 DEBUG GRAPH - generateGraphData called with:', {
      billsCount: (bills || []).length,
      paysCount: filteredPays.length,
      paysData: filteredPays.map(p => ({ id: p.id, nickname: p.nickname, nextPay: p.next_pay || p.due_date, transferAmount: p.transferAmount, description: p.description })),
      overdueBillsCount: (overdueBills || []).length,
      dateRange: { start: formatDate(startDate), end: formatDate(endDate) }
    });

    // Attempt to include additional future pays up to the end of the window (no duplicates)
    if (filteredPays.length > 0 && state && state.pays && Array.isArray(state.pays)) {
      const latestPayDate = filteredPays.reduce((latest, pay) => {
        if (!pay || !pay.due_date) return latest;
        const payDate = parseDateOnly(pay.due_date);
        return payDate > latest ? payDate : latest;
      }, new Date(0));

      if (latestPayDate < endYMD) {
        const additionalPays = state.pays.filter(pay => {
          if (!pay || !pay.due_date) return false;
          const payDate = parseDateOnly(pay.due_date);
          return pay.description === 'incoming pay' && payDate > latestPayDate && payDate <= endYMD;
        });

        if (additionalPays.length > 0) {
          // Merge unique by id
          const existingIds = new Set(filteredPays.map(p => p.id));
          const merged = [...filteredPays];
          additionalPays.forEach(p => {
            if (!existingIds.has(p.id)) merged.push(p);
          });
          filteredPays = merged;
        }
      }
    }

    // Generate data for days in the window
    const data = [];
    let currentBalance = parseFloat(initialBalance) || 0;

    // Get all unpaid bills due before the start date
    const priorUnpaidBills = filteredBills.filter(bill => {
      const billDate = parseDateOnly(bill.due_date);
      return !bill.paid && billDate < startYMD;
    });

    // Get all unpaid transfers due before the start date
    const priorUnpaidTransfers = filteredPays.filter(pay => {
      if (!pay) return false;
      const payDate = parseDateOnly(pay.due_date || pay.next_pay);
      const isTransferMade = pay.transferMade || pay.paid;
      return !isTransferMade && payDate < startYMD;
    });

    for (let cursor = new Date(startYMD); cursor <= endYMD; cursor.setDate(cursor.getDate() + 1)) {
      const dateYMD = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
      const dateStr = formatDate(dateYMD);

      // Bills for this day
      const dailyBills = filteredBills.filter(bill => {
        if (bill.paid && bill.paid_date) {
          return isSameYMD(parseDateOnly(bill.paid_date), dateYMD);
        }
        return isSameYMD(parseDateOnly(bill.due_date), dateYMD);
      });

      // Transfers for this day
      const dailyTransfers = filteredPays.filter(pay => {
        if (!pay) return false;
        // Use next_pay if due_date is not set, as transfers might use either field
        const payDueDate = pay.due_date || pay.next_pay;
        if (!payDueDate) return false;
        
        // Check if transfer is made/paid
        const isTransferMade = pay.transferMade || pay.paid;
        if (!isTransferMade) {
          return isSameYMD(parseDateOnly(payDueDate), dateYMD);
        }
        if (isTransferMade && pay.paid_date) {
          return isSameYMD(parseDateOnly(pay.paid_date), dateYMD);
        }
        return false;
      });

      // Debug logging for specific dates
      if (dateStr.includes('2024-01-14') || dailyTransfers.length > 0) {
        debugLog(`🔍 DEBUG GRAPH - Date ${dateStr}:`, {
          dailyTransfersCount: dailyTransfers.length,
          transfers: dailyTransfers.map(t => ({ 
            id: t.id, 
            nickname: t.nickname, 
            dueDate: t.due_date, 
            nextPay: t.next_pay,
            transferAmount: t.transferAmount, 
            transferMade: t.transferMade, 
            paidDate: t.paid_date 
          }))
        });
      }

      // Calculate expenses and income
      const dailyExpenses = dailyBills.reduce((sum, bill) => sum + Math.abs(parseFloat(bill.amount) || 0), 0);
      const dailyIncome = dailyTransfers.reduce((sum, tr) => sum + Math.abs(parseFloat(tr.amount || tr.transferAmount || 0) || 0), 0);

      let todaysExpenses = dailyExpenses;
      let todaysIncome = dailyIncome;

      if (isSameYMD(dateYMD, startYMD)) {
        // Include prior unpaid (not due today) on first day
        const priorBillsNotToday = priorUnpaidBills.filter(bill => !isSameYMD(parseDateOnly(bill.due_date), dateYMD));
        const priorTransfersNotToday = priorUnpaidTransfers.filter(tr => !isSameYMD(parseDateOnly(tr.due_date), dateYMD));

        todaysExpenses += priorBillsNotToday.reduce((sum, bill) => sum + Math.abs(parseFloat(bill.amount) || 0), 0);
        todaysIncome += priorTransfersNotToday.reduce((sum, tr) => sum + Math.abs(parseFloat(tr.amount || tr.transferAmount || 0) || 0), 0);
      }

      const endBalance = currentBalance + todaysIncome - todaysExpenses;

      // Compose details for tooltips
      const billDetails = isSameYMD(dateYMD, startYMD)
        ? [
            ...dailyBills,
            ...priorUnpaidBills.filter(bill => !isSameYMD(parseDateOnly(bill.due_date), dateYMD)),
          ]
        : dailyBills;
        
      const transferDetails = isSameYMD(dateYMD, startYMD)
        ? [
            ...dailyTransfers,
            ...priorUnpaidTransfers.filter(tr => !isSameYMD(parseDateOnly(tr.due_date), dateYMD)),
          ]
        : dailyTransfers;

      data.push({
        date: dateStr,
        startBalance: currentBalance,
        income: todaysIncome,
        expenses: todaysExpenses,
        endBalance,
        billDetails,
        transferDetails,
      });
      
      currentBalance = endBalance;
    }

    return data;
  };

  // function to handle window updates
  const updateGraphDataWindow = async (direction) => {
    const extensionDays = 28; // Add 28 more days when extending

    debugLog('🔍 DEBUG GRAPH WINDOW - updateGraphDataWindow called:', {
      direction,
      currentWeekIndex,
      graphDataLength: graphDataWindow.data.length,
      currentEnd: graphDataWindow.end.toISOString().split('T')[0]
    });

    if (direction === 'forward') {
      debugLog('🚀 Extending graph window forward...');
      const newEnd = new Date(graphDataWindow.end);
      newEnd.setDate(newEnd.getDate() + extensionDays);

      const newData = await generateGraphData(
        state.bills,
        state.pays,
        state.overdueBills,
        state.currentBalance,
        graphDataWindow.start,
        newEnd
      );

      debugLog('🚀 Graph window extended forward:', {
        oldEnd: graphDataWindow.end.toISOString().split('T')[0],
        newEnd: newEnd.toISOString().split('T')[0],
        oldDataLength: graphDataWindow.data.length,
        newDataLength: newData.length
      });

      setGraphDataWindow(prev => ({
        ...prev,
        end: newEnd,
        data: newData
      }));
    }

    if (direction === 'backward') {
      debugLog('🚀 Extending graph window backward...');
      const newStart = new Date(graphDataWindow.start);
      newStart.setDate(newStart.getDate() - extensionDays);

      // Don't go before today
      const today = new Date();
      if (newStart < today) {
        newStart.setTime(today.getTime());
      }

      const newData = await generateGraphData(
        state.bills,
        state.pays,
        state.overdueBills,
        state.currentBalance,
        newStart,
        graphDataWindow.end
      );

      debugLog('🚀 Graph window extended backward:', {
        oldStart: graphDataWindow.start.toISOString().split('T')[0],
        newStart: newStart.toISOString().split('T')[0],
        oldDataLength: graphDataWindow.data.length,
        newDataLength: newData.length
      });

      setGraphDataWindow(prev => ({
        ...prev,
        start: newStart,
        data: newData
      }));
    }
  };

  // Effect to handle graph window updates when navigating
  useEffect(() => {
    if (currentWeekIndex > 0) {
      updateGraphDataWindow('forward');
    }
  }, [currentWeekIndex]);

  // Trigger extension when approaching the end of the data window
  useEffect(() => {
    const daysIntoWindow = currentWeekIndex * 7; // Each week = 7 days
    const totalDays = graphDataWindow.data?.length || 0;
    const remainingDays = totalDays - daysIntoWindow;
    
    debugLog('🔍 DEBUG GRAPH NAVIGATION - Buffer check:', {
      currentWeekIndex,
      daysIntoWindow,
      totalDays,
      remainingDays,
      shouldExtend: remainingDays <= 14 && remainingDays > 0 && currentWeekIndex > 0
    });
    
    // Extend when we have 14 days or less remaining and we're not at the start
    if (remainingDays <= 14 && remainingDays > 0 && currentWeekIndex > 0) {
      debugLog('🚀 Triggering extension - approaching end of data');
      updateGraphDataWindow('forward');
    }
  }, [currentWeekIndex, graphDataWindow.data?.length]);

  // Define date variables here after the state is set
  const {
    firstWeekStart,
    firstWeekEnd,
    secondWeekStart,
    secondWeekEnd,
    thirdWeekStart,
    thirdWeekEnd,
  } = state.fortnightDates;

  const currentWeekStart = determineCurrentWeek(state.fortnightDates) === "first" ? new Date(firstWeekStart) : new Date(secondWeekStart);
  const currentWeekEnd = determineCurrentWeek(state.fortnightDates) === "first" ? new Date(firstWeekEnd) : new Date(secondWeekEnd);
  const nextWeekStart = determineCurrentWeek(state.fortnightDates) === "first" ? new Date(secondWeekStart) : new Date(thirdWeekStart);
  const nextWeekEnd = determineCurrentWeek(state.fortnightDates) === "first" ? new Date(secondWeekEnd) : new Date(thirdWeekEnd);

  // Determine the start and end dates for weeks based on fortnight dates
  const today = new Date();

  // Adjust nextWeekEnd to include the last day of the fortnight
  const adjustedNextWeekEnd = new Date(nextWeekEnd);
  adjustedNextWeekEnd.setDate(adjustedNextWeekEnd.getDate() + 1);

// TODO: use the below to see if it solves the date at top of each table.
  useEffect(() => {
    const formatDates = async () => {
        try {
          if (today >= new Date(firstWeekStart) && today <= new Date(firstWeekEnd)) {
            let currentWeekStart = new Date(firstWeekStart);
            let currentWeekEnd = new Date(firstWeekEnd);
            let nextWeekStart = new Date(secondWeekStart);
            let nextWeekEnd = new Date(secondWeekEnd);
          } else if (today >= new Date(secondWeekStart) && today <= new Date(secondWeekEnd)) {
            let currentWeekStart = new Date(secondWeekStart);
            let currentWeekEnd = new Date(secondWeekEnd);
            let nextWeekStart = new Date(thirdWeekStart);
            let nextWeekEnd = new Date(thirdWeekEnd);
          }
    } catch (error) {
          debugError('Error fetching dates to format:', error.message);
          debugError('Error fetching dates to format (full details):', error);
        }
      };

//      calculateTotals();
  }, [state.fortnightDates]);

  // Fetch bills and update the state based on the active tab and title
  useEffect(() => {
    const fetchAndSetBills = async () => {
      try {
        if (loading) return; // Avoid fetching bills while loading initial data

        // OPTIMIZATION: Skip for fortnight tab since fetchInitialData() already handles all data
        if (activeTab === "#fortnight") {
          debugLog('🟢 SKIPPING fetchAndSetBills for fortnight tab - fetchInitialData handles this');
          return;
        }

        // Skip for search tab to avoid wiping fortnight state with empty defaults
        if (activeTab === "#search") {
          debugLog('🟢 SKIPPING fetchAndSetBills for search tab');
          return;
        }

        // Initialize all variables with defaults
        let currentWeekBills = [];
        let nextWeekBills = [];
        let currentWeekUnpaidBills = [];
        let currentWeekPaidBills = [];
        let nextWeekUnpaidBills = [];
        let nextWeekPaidBills = [];
        let prevUnpaidBills = [];
        let filteredBills = [];
        let totalUnpaidCurrentWeek = 0;
        let totalUnpaidNextWeek = 0;
        let mappedCurrentWeekPays = [];
        let mappedNextWeekPays = [];
        let mappedFuturePays = [];
        let mappedPays = [];

        if (activeTab === "#future") {
          try {
            setFutureBillsPage(1); // Reset to page 1 when switching to the tab
            
            // Get future bills from API with pagination
            const futureResult = await getFutureMonthBills(1, BILLS_PER_PAGE);
            
            // Check if we got valid data before proceeding
            if (!futureResult || !Array.isArray(futureResult.bills)) {
              debugError('Error: getFutureMonthBills did not return valid data for pagination', futureResult);
              filteredBills = [];
        } else {
              // Filter by bank account if a specific account is selected
              filteredBills = !showAllBills && state.bankAccountId 
                ? futureResult.bills.filter(bill => bill.bank_account === state.bankAccountId) 
                : futureResult.bills;
              
              // Sort bills by due date
              filteredBills = filteredBills.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
              
              // Store pagination info
              setFutureBillsTotal(futureResult.total);
              setFutureBillsHasMore(futureResult.hasMore);
            }
          } catch (error) {
            debugError('Error fetching future bills:', error.message);
            debugError('Error fetching future bills (full details):', error);
            filteredBills = [];
          }
        } else if (activeTab === "#recent") {
          try {
            setRecentBillsPage(1); // Reset to page 1 when switching to the tab
            
            // Calculate date range for recently paid bills (last month)
            const previousMonth = new Date();
            previousMonth.setUTCMonth(previousMonth.getUTCMonth() - 1);
            const monthBeforeLastWed = new Date(previousMonth);
            monthBeforeLastWed.setUTCDate(monthBeforeLastWed.getUTCDate() - ((monthBeforeLastWed.getUTCDay() + 6) % 7));
            
            // Get all bills with pagination 
            const result = await getBills(
              null,
              true, // Only get paid bills
              'paid_date',
              'desc',
              !showAllBills && state.bankAccountId ? state.bankAccountId : null,
              1,
              BILLS_PER_PAGE
            );
            
            // Check if result is valid before processing
            if (!result || !Array.isArray(result.bills)) {
              debugError('Error: getBills did not return valid data for recent bills', result);
              filteredBills = [];
              setRecentBillsTotal(0);
              setRecentBillsHasMore(false);
        } else {
              // Filter for paid bills within the time range
              let recentPaidBills = result.bills.filter(bill => 
                bill.paid_date && new Date(bill.paid_date) > monthBeforeLastWed
              );
              
              // Set filtered bills
              filteredBills = recentPaidBills;
              
              // Store pagination info
              setRecentBillsTotal(result.total);
              setRecentBillsHasMore(result.hasMore);
            }
          } catch (error) {
            debugError('Error fetching recently paid bills:', error.message);
            debugError('Error fetching recently paid bills (full details):', error);
            filteredBills = [];
            setRecentBillsTotal(0);
            setRecentBillsHasMore(false);
          }
        }

        // Update state if values have changed
        setState(prevState => {
          if (
            prevState.currentWeekBills !== currentWeekBills ||
            prevState.nextWeekBills !== nextWeekBills ||
            prevState.filteredBills !== filteredBills ||
            prevState.currentWeekPaidBills !== currentWeekPaidBills ||
            prevState.nextWeekPaidBills !== nextWeekPaidBills
          ) {
        return {
          ...prevState,
              currentWeekBills,
              nextWeekBills,
              filteredBills,
              currentWeekPaidBills,
              nextWeekPaidBills,
              transferTotals: {
                totalUnpaidPaysThisWeek: totalUnpaidCurrentWeek,
                totalUnpaidPaysNextWeek: totalUnpaidNextWeek,
                totalUnpaidPaysThisFortnight: totalUnpaidCurrentWeek + totalUnpaidNextWeek, // Calculate totalFortnight
              },
            };
          }
          return prevState; // Return previous state if no changes
        });

    } catch (error) {
        debugError('Error fetching filtered bills:', error.message);
        debugError('Error fetching filtered bills (full details):', error);
      }
    };


    fetchAndSetBills();
  }, [
    activeTab, 
    title, 
    currentDate, 
    showAllBills, 
    state.bankAccountId
    // Removed dependencies that cause re-fetching and state overrides:
    // - state.bills
    // - loading 
    // - payCache
  ]);

  useEffect(() => {
  }, [state.currentWeekPaidBills]);

  // Consolidate function to calculate totals when bills are present
  useEffect(() => {
      const calculateTotals = async () => {
        try {
          // Always fetch totals if we have a specific bank account selected (needed for tracking period calculations)
          if (state.bankAccountId && !showAllBills) {
            
            debugLog('🔵 FETCHING INDIVIDUAL TOTALS for tracking cards');
            
            // Pass the bankAccountId to the total calculation functions
            const [totalThisWeek, totalNextWeek, totalFortnight] = await Promise.all([
              getTotalThisWeek(state.bankAccountId),
              getTotalNextWeek(state.bankAccountId),
              getTotalFortnight(state.bankAccountId),
            ]);

            setState(prevState => ({
              ...prevState,
              totals: {
                ...prevState.totals,
                totalThisWeek,
                totalNextWeek,
                totalFortnight,
              },
            }));
        } else if (showAllBills || activeTab === "#fortnight") {
            // For all bills view or fortnight tab, reset totals or don't calculate them
            // (fortnight tab gets totals from the API, all bills doesn't need totals)
            debugLog('🟡 SKIPPING INDIVIDUAL TOTALS - using API data or all bills mode');
            
            if (showAllBills) {
            setState(prevState => ({
              ...prevState,
              totals: {
                ...prevState.totals,
                totalThisWeek: 0,
                totalNextWeek: 0,
                totalFortnight: 0,
              },
            }));
            }
          }
        } catch (error) {
          debugError('Error fetching totals (Bills.js in calculateTotals):', error.message);
          debugError('Error fetching totals (Bills.js in calculateTotals) (full details):', error);
        }
      };

      calculateTotals();
  }, [
    activeTab,  // Add activeTab as dependency
    state.bankAccountId, 
    showAllBills
    // Removed state.bills dependency to reduce unnecessary calls
  ]);
  
  // Add a separate effect to refresh the balance when bills or transfers change paid status
  useEffect(() => {
    // Skip redundant updates for fortnight tab since new backend filtering handles everything
    if (activeTab === "#fortnight") {
      debugLog('🟢 SKIPPING BALANCE/GRAPH UPDATE - fortnight tab uses new backend filtering');
      return;
    }
    
    // We only want to refresh the balance if a specific bank account is selected
    if (state.bankAccountId && !showAllBills) {
      fetchBalance();
      
      // Also regenerate graph data when paid status changes
      const newGraphData = generateGraphData(
        state.bills,
        state.pays,
        state.currentBalance,
        graphDataWindow.start,
        graphDataWindow.end
      );
      
      if (Array.isArray(newGraphData) && newGraphData.length > 0) {
        setGraphData(newGraphData);
      }
    }
  }, [
    state.currentWeekPaidBills, 
    state.nextWeekPaidBills,
    state.pays, // Add pays to the dependency array to trigger updates when transfers change
    state.transferTotals, // Add transferTotals to update when totals change
    activeTab // Add activeTab to dependency array
  ]);

  const formattedEndFirstWeek = formatReadableDate(currentWeekEnd);
  const formattedStartNextWeek = formatReadableDate(nextWeekStart);
  const formattedRangeFirstWeek = formatShortDateRange(currentWeekStart, currentWeekEnd);
  const formattedRangeSecondWeek = formatShortDateRange(nextWeekStart, nextWeekEnd);
  const formattedStartNextFort = formatReadableDate(nextWeekEnd);

  const handleBalanceUpdateForFortnightTab = (updatedBill, balanceChangeAmount) => {
    const updatedBillIndex = state.filteredBills.findIndex((bill) => bill.id === updatedBill.id);
    if (updatedBillIndex !== -1) {
      const updatedFilteredBills = [...state.filteredBills];
      updatedFilteredBills[updatedBillIndex] = updatedBill;
      
      setState(prevState => ({
          ...prevState,
        filteredBills: updatedFilteredBills,
      }));
    }
    
    // Don't call updateBalanceForBill here as it will be called by handleUpdatePaidDate
  };

  // Add a dedicated effect to update UI elements when transferTotals change
  useEffect(() => {
    // This effect will trigger any time the transferTotals are updated
    
    // No need to do anything else - just having the dependency
    // is enough to trigger rerenders of components using this data
  }, [state.transferTotals]);

  // Modify the handleUpdatePaidDate function to improve handling of transfer status changes
  const handleUpdatePaidDate = async (updatedBill) => {
    const updatedBillIndex = state.filteredBills.findIndex((bill) => bill.id === updatedBill.id);
    let balanceChangeAmount;
  
    const wasPaid = state.filteredBills[updatedBillIndex]?.paid;
    const isPaid = updatedBill.paid;
    const isPay = updatedBill.description === "incoming pay";
    
    // For consistency, ensure transferMade is also set for pay items
    if (isPay) {
      updatedBill.transferMade = isPaid;
    }
  
    if (!updatedBill.paid) {
      updatedBill.paid_date = null;
    }

    balanceChangeAmount = parseFloat(updatedBill.amount);
    
    // Immediately generate new graph data with the updated bill/pay to ensure it's displayed right away
    if (isPay) {
      // Create an updated version of pays for the graph data
      const updatedPays = state.pays.map(pay => 
        pay && pay.id === updatedBill.id 
          ? { ...pay, paid: isPaid, transferMade: isPaid } 
          : pay
      );
      
      // Generate and update graph data immediately
      const newGraphData = generateGraphData(
        state.bills,
        updatedPays,
        state.overdueBills,
        state.currentBalance,
        graphDataWindow.start,
        graphDataWindow.end
      );
      
      if (Array.isArray(newGraphData) && newGraphData.length > 0) {
        setGraphData(newGraphData);
        setGraphDataWindow(prev => ({
          ...prev,
          data: newGraphData
        }));
      }
    } else {
      // For regular bills (including overdue), also regenerate graph data
      const updatedBills = state.bills.map(bill => 
        bill.id === updatedBill.id ? updatedBill : bill
      );
      
      // Update overdue bills array for graph regeneration
      const updatedOverdueBills = state.overdueBills.map(bill =>
        bill.id === updatedBill.id ? updatedBill : bill
      ).filter(bill => !bill.paid); // Remove paid bills from overdue
      
      const newGraphData = generateGraphData(
        updatedBills,
        state.pays,
        updatedOverdueBills,
        state.currentBalance,
        graphDataWindow.start,
        graphDataWindow.end
      );
      
      if (Array.isArray(newGraphData) && newGraphData.length > 0) {
        setGraphData(newGraphData);
        setGraphDataWindow(prev => ({
          ...prev,
          data: newGraphData
        }));
      }
    }

    // First update the state
    setState(prevState => {
      let newState = { ...prevState };

      // Update the bills array first
      newState.bills = newState.bills.map(bill =>
        bill.id === updatedBill.id ? updatedBill : bill
      );

      // Update overdue bills array if this bill is in it
      if (newState.overdueBills && newState.overdueBills.length > 0) {
        const overdueIndex = newState.overdueBills.findIndex(bill => bill.id === updatedBill.id);
        if (overdueIndex !== -1) {
          if (isPaid && !isPay) {
            // If it's a regular bill being marked as paid, remove it from overdue
            newState.overdueBills = newState.overdueBills.filter(bill => bill.id !== updatedBill.id);
            // Also update the total count
            newState.overdueTotal = Math.max(0, (newState.overdueTotal || 0) - 1);
          } else {
            // Otherwise just update it in place
            newState.overdueBills = newState.overdueBills.map(bill =>
              bill.id === updatedBill.id ? updatedBill : bill
            );
          }
        }
      }

      // If this is a pay (transfer), update the pays array too
      if (isPay) {
        // If we're marking as paid and don't have a paid_date, set it to today
        if (isPaid && !updatedBill.paid_date) {
          updatedBill.paid_date = new Date().toISOString().split('T')[0];
        }

        newState.pays = newState.pays.map(pay => {
          if (pay && pay.id === updatedBill.id) {
            // Update the transferMade property to match the paid status
            return {
              ...pay,
              paid: updatedBill.paid,
              paid_date: updatedBill.paid_date,
              transferMade: updatedBill.paid, // Make sure both are updated
            };
          }
          return pay;
        });
        
        // Make sure to update transfer totals immediately
        if (newState.currentWeekBills.some(bill => bill.id === updatedBill.id)) {
          newState.transferTotals = {
            ...newState.transferTotals,
            totalUnpaidPaysThisWeek: isPaid
              ? newState.transferTotals.totalUnpaidPaysThisWeek - balanceChangeAmount
              : newState.transferTotals.totalUnpaidPaysThisWeek + balanceChangeAmount,
            totalUnpaidPaysThisFortnight: isPaid
              ? newState.transferTotals.totalUnpaidPaysThisFortnight - balanceChangeAmount
              : newState.transferTotals.totalUnpaidPaysThisFortnight + balanceChangeAmount
          };
        } else if (newState.nextWeekBills.some(bill => bill.id === updatedBill.id)) {
          newState.transferTotals = {
            ...newState.transferTotals,
            totalUnpaidPaysNextWeek: isPaid
              ? newState.transferTotals.totalUnpaidPaysNextWeek - balanceChangeAmount
              : newState.transferTotals.totalUnpaidPaysNextWeek + balanceChangeAmount,
            totalUnpaidPaysThisFortnight: isPaid
              ? newState.transferTotals.totalUnpaidPaysThisFortnight - balanceChangeAmount
              : newState.transferTotals.totalUnpaidPaysThisFortnight + balanceChangeAmount
          };
        }
      }

      // Check if the bill previously existed in the current or next week paid bills
      const wasInCurrentWeekPaid = newState.currentWeekPaidBills.some(bill => bill.id === updatedBill.id);
      const wasInNextWeekPaid = newState.nextWeekPaidBills.some(bill => bill.id === updatedBill.id);
      
      // Update filtered collections
      if (updatedBillIndex !== -1) {
        if (isPaid) {
          // If bill is now paid, filter it out of filteredBills if it's not a pay
          if (!isPay) {
            newState.filteredBills = newState.filteredBills.filter(bill => bill.id !== updatedBill.id);
          } else {
            // If it's a pay, just update it
            newState.filteredBills[updatedBillIndex] = updatedBill;
          }
        } else {
          // If bill is unpaid, update it in place
          newState.filteredBills[updatedBillIndex] = updatedBill;
        }
      }

      const isCurrentWeek = newState.currentWeekBills.some(bill => bill.id === updatedBill.id);
      const isNextWeek = newState.nextWeekBills.some(bill => bill.id === updatedBill.id);
      
      // Check if the bill's due date falls within current week
      const billDueDate = new Date(updatedBill.due_date);
      const currentWeekStart = determineCurrentWeek(newState.fortnightDates) === "first" ? 
        new Date(newState.fortnightDates.firstWeekStart) : new Date(newState.fortnightDates.secondWeekStart);
      const currentWeekEnd = determineCurrentWeek(newState.fortnightDates) === "first" ? 
        new Date(newState.fortnightDates.firstWeekEnd) : new Date(newState.fortnightDates.secondWeekEnd);
      const nextWeekStart = determineCurrentWeek(newState.fortnightDates) === "first" ? 
        new Date(newState.fortnightDates.secondWeekStart) : new Date(newState.fortnightDates.thirdWeekStart);
      const nextWeekEnd = determineCurrentWeek(newState.fortnightDates) === "first" ? 
        new Date(newState.fortnightDates.secondWeekEnd) : new Date(newState.fortnightDates.thirdWeekEnd);
        
      const billInCurrentWeekRange = billDueDate >= currentWeekStart && billDueDate <= currentWeekEnd;
      const billInNextWeekRange = billDueDate >= nextWeekStart && billDueDate <= nextWeekEnd;
      const billBeforeCurrentWeek = billDueDate < currentWeekStart;
  
      if (isCurrentWeek) {
        if (isPaid && !isPay) {
          // If it's a regular bill and now paid, remove it from currentWeekBills
          newState.currentWeekBills = newState.currentWeekBills.filter(bill => bill.id !== updatedBill.id);
        } else if (isPay) {
          // If it's a pay, just update it
          newState.currentWeekBills = newState.currentWeekBills.map(bill => 
            bill.id === updatedBill.id ? updatedBill : bill
          );
        } else {
          // If it's unpaid, update it
          newState.currentWeekBills = newState.currentWeekBills.map(bill => 
            bill.id === updatedBill.id ? updatedBill : bill
          );
        }
        
        if (updatedBill.paid) {
          newState.currentWeekPaidBills = [
            ...newState.currentWeekPaidBills.filter(bill => bill.id !== updatedBill.id),
            updatedBill
          ];
        } else {
          newState.currentWeekPaidBills = newState.currentWeekPaidBills.filter(bill => bill.id !== updatedBill.id);
        }
      } else if (isNextWeek) {
        if (isPaid && !isPay) {
          // If it's a regular bill and now paid, remove it from nextWeekBills
          newState.nextWeekBills = newState.nextWeekBills.filter(bill => bill.id !== updatedBill.id);
        } else if (isPay) {
          // If it's a pay, just update it
          newState.nextWeekBills = newState.nextWeekBills.map(bill => 
            bill.id === updatedBill.id ? updatedBill : bill
          );
        } else {
          // If it's unpaid, update it
          newState.nextWeekBills = newState.nextWeekBills.map(bill => 
            bill.id === updatedBill.id ? updatedBill : bill
          );
        }
        
        if (updatedBill.paid) {
          newState.nextWeekPaidBills = [
            ...newState.nextWeekPaidBills.filter(bill => bill.id !== updatedBill.id),
            updatedBill
          ];
        } else {
          newState.nextWeekPaidBills = newState.nextWeekPaidBills.filter(bill => bill.id !== updatedBill.id);
        }
      } else {
        // Handle the case where the bill was previously in paid bills but now is being marked as unpaid
        // We need to add it to the appropriate unpaid bills list
        if (!isPaid) {
          // If bill was in currentWeekPaid but isn't anymore, add it to currentWeekBills
          if (wasInCurrentWeekPaid) {
            newState.currentWeekPaidBills = newState.currentWeekPaidBills.filter(bill => bill.id !== updatedBill.id);
            
            // Add to currentWeekBills if due date is in current week or before
            if (billInCurrentWeekRange || billBeforeCurrentWeek) {
              // Only add if not already present
              if (!newState.currentWeekBills.some(bill => bill.id === updatedBill.id)) {
                newState.currentWeekBills = [...newState.currentWeekBills, updatedBill].sort(
                  (a, b) => new Date(a.due_date) - new Date(b.due_date)
                );
              }
            }
          }
          
          // If bill was in nextWeekPaid but isn't anymore, add it to nextWeekBills
          if (wasInNextWeekPaid) {
            newState.nextWeekPaidBills = newState.nextWeekPaidBills.filter(bill => bill.id !== updatedBill.id);
            
            // Add to nextWeekBills if due date is in next week
            if (billInNextWeekRange) {
              // Only add if not already present
              if (!newState.nextWeekBills.some(bill => bill.id === updatedBill.id)) {
                newState.nextWeekBills = [...newState.nextWeekBills, updatedBill].sort(
                  (a, b) => new Date(a.due_date) - new Date(b.due_date)
                );
              }
            }
          }
        } else {
          // Handle case where bill remains paid but other details may have changed
          // Update the bill in paid collections if it exists there
          if (wasInCurrentWeekPaid) {
            newState.currentWeekPaidBills = newState.currentWeekPaidBills.map(bill => 
              bill.id === updatedBill.id ? updatedBill : bill
            );
          }
          
          if (wasInNextWeekPaid) {
            newState.nextWeekPaidBills = newState.nextWeekPaidBills.map(bill => 
              bill.id === updatedBill.id ? updatedBill : bill
            );
          }
        }
      }

      return newState;
    });

    // Update the bill in the fortnight tab if needed
    if (activeTab === "#fortnight") {
      handleBalanceUpdateForFortnightTab(updatedBill, balanceChangeAmount);
    }
    
    // Always update the balance if paid status changed and we have a bank account
    // For transfers, use state.bankAccountId; for bills, use updatedBill.bank_account
    const targetBankAccountId = isPay ? (state.bankAccountId || bankAccountId) : updatedBill.bank_account;
    
    if (wasPaid !== isPaid && targetBankAccountId) {
      try {
        // Get the correct bank account ID (bills have it directly, pays need bankAccountId param)
        const accountId = isPay ? targetBankAccountId : updatedBill.bank_account;
        
        // Get the balance for the bill's bank account
        const bankAccountBalance = await getBalance(accountId);
        const currentBalanceAmount = parseFloat(bankAccountBalance.amount);
        let newBalance;
    
        if (isPay) {
          // For pays: add when paid, subtract when unpaid
          newBalance = isPaid ? currentBalanceAmount + balanceChangeAmount : currentBalanceAmount - balanceChangeAmount;
          
          // Set paid_date for transfers when they're marked as paid
          if (isPay && isPaid && !updatedBill.paid_date) {
            updatedBill.paid_date = new Date().toISOString().split('T')[0];
          }
        } else {
          // For bills: subtract when paid, add when unpaid
          newBalance = isPaid ? currentBalanceAmount - balanceChangeAmount : currentBalanceAmount + balanceChangeAmount;
        }
        
        // Format the amount as a string with 2 decimal places
        const formattedNewBalance = newBalance.toFixed(2);
        
        // Update the balance for the bill's bank account with complete data
        await updateBalance(bankAccountBalance.id, {
          amount: formattedNewBalance,
          bank_account: accountId,
          update_date: new Date().toISOString().split('T')[0]
        });
        
        // If we're viewing this specific bank account, update the state
        if (!showAllBills && state.bankAccountId === accountId) {
          const updatedBalance = await getBalance(state.bankAccountId);
          setState(prevState => ({
            ...prevState,
            balance: parseFloat(updatedBalance.amount),
            currentBalance: parseFloat(updatedBalance.amount),
          }));
        }

        // For pay items (transfers), force a refresh of transfer totals UI by updating state
        if (isPay) {
          // Force UI refresh for transfer totals by making a minimal state update
          setState(prevState => ({
          ...prevState,
            transferTotals: {
              ...prevState.transferTotals
            }
          }));
        }
    } catch (error) {
        debugError(`Error updating balance for bank account:`, error.message);
        debugError(`Error updating balance for bank account (full details):`, error);
        // Show more error details if available
        if (error.response && error.response.data) {
          debugError('API error details:', error.response.data);
        }
      }
    }
  };

  // Display loading message while loading
  if (loading) {
    return <div>Loading...</div>;
  }


const handleBillAdded = async () => {
  setShowAddBillModal(false);
  setLoading(true);
  try {
    // Fetch updated bills list
    const billsResponse = await getBills();
    
    // Extract the bills array from the response object
    const allBills = billsResponse.bills || billsResponse;
    
    // Ensure allBills is an array before updating state
    if (!Array.isArray(allBills)) {
      debugError('Error: getBills did not return valid bills data', billsResponse);
      return;
    }
    
    // Update state with new bills
    setState(prevState => ({
      ...prevState,
      bills: allBills
    }));
    
    // If we're on the fortnight tab, refresh the current and next week bills
    if (activeTab === "#fortnight") {
      await fetchInitialData();
    }
  } catch (error) {
    debugError('Error refreshing bills after adding:', error.message);
    debugError('Error refreshing bills after adding (full details):', error);
  } finally {
    setLoading(false);
  }
};

const handlePayAdded = async () => {
  setShowAddPayModal(false);
  setLoading(true);
  try {
    const allPays = await getPays();
    setState(prevState => ({
          ...prevState,
      pays: allPays
    }));
  } finally {
    setLoading(false);
  }
};

// Add these functions to load more bills for the future and recently paid tabs

const loadMoreFutureBills = async () => {
  if (!futureBillsHasMore) return;
  
  try {
    const nextPage = futureBillsPage + 1;
    
    // Get future bills from API with pagination
    const futureResult = await getFutureMonthBills(nextPage, BILLS_PER_PAGE);
    
    // Check if we got valid data before proceeding
    if (!futureResult || !Array.isArray(futureResult.bills)) {
      debugError('Error: getFutureMonthBills did not return valid data for pagination', futureResult);
      return;
    }
    
    // Filter by bank account if a specific account is selected
    const newBills = !showAllBills && state.bankAccountId 
      ? futureResult.bills.filter(bill => bill.bank_account === state.bankAccountId) 
      : futureResult.bills;
    
    // Append to current filtered bills
    setState(prevState => ({
      ...prevState,
      filteredBills: [...prevState.filteredBills, ...newBills].sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    }));
    
    // Update pagination info
    setFutureBillsPage(nextPage);
    setFutureBillsHasMore(futureResult.hasMore);
    
    } catch (error) {
    debugError('Error loading more future bills:', error.message);
    debugError('Error loading more future bills (full details):', error);
  }
};

const loadMoreRecentBills = async () => {
  if (!recentBillsHasMore) return;
  
  try {
    const nextPage = recentBillsPage + 1;
    
    // Get recent bills from API with pagination
    const result = await getBills(
      null,
      true, // Only get paid bills
      'paid_date',
      'desc',
      !showAllBills && state.bankAccountId ? state.bankAccountId : null,
      nextPage,
      BILLS_PER_PAGE
    );
    
    // Check if result is valid and has a bills property before proceeding
    if (!result || !Array.isArray(result.bills)) {
      debugError('Error: getBills did not return valid data for pagination of recent bills', result);
      return;
    }
    
    // Calculate date range for recently paid bills
    const previousMonth = new Date();
    previousMonth.setUTCMonth(previousMonth.getUTCMonth() - 1);
    const monthBeforeLastWed = new Date(previousMonth);
    monthBeforeLastWed.setUTCDate(monthBeforeLastWed.getUTCDate() - ((monthBeforeLastWed.getUTCDay() + 6) % 7));
    
    // Filter for paid bills within the time range
    let newRecentPaidBills = result.bills.filter(bill => 
      bill.paid_date && new Date(bill.paid_date) > monthBeforeLastWed
    );
    
    // Append to current filtered bills
    setState(prevState => ({
      ...prevState,
      filteredBills: [...prevState.filteredBills, ...newRecentPaidBills]
    }));
    
    // Update pagination info
    setRecentBillsPage(nextPage);
    setRecentBillsHasMore(result.hasMore);
    
  } catch (error) {
    debugError('Error loading more recent bills:', error.message);
    debugError('Error loading more recent bills (full details):', error);
  }
};

// Overdue pagination: handle page change (displayed page maps to API page index)
const handleOverduePageChange = async (displayedPage) => {
  try {
    // Mark loading and compute context key
    const fortnight = state.fortnightDates;
    const cutoffDate = (determineCurrentWeek(fortnight) === 'first'
      ? new Date(fortnight.firstWeekStart)
      : new Date(fortnight.secondWeekStart)
    ).toISOString().split('T')[0];
    const bankId = state.bankAccountId || null; // always filter when an account is selected
    const contextKey = `${cutoffDate}|${bankId || ''}`;
    setState(prev => ({ ...prev, overdueLoading: true, overdueContextKey: contextKey }));

    const totalPages = state.overdueTotalPages || 1;
    const apiPage = Math.max(1, Math.min(totalPages, totalPages - displayedPage + 1));
    const resp = await getOverdueBills(cutoffDate, bankId, apiPage, state.overdueLimit || 20);
    // Ignore if context changed mid-flight
    setState(prev => {
      if (prev.overdueContextKey !== contextKey) return prev;
      return {
      ...prev,
      overdueBills: Array.isArray(resp.bills) ? resp.bills : [],
      overduePageApi: apiPage,
      overdueDisplayedPage: displayedPage,
        overdueTotal: resp.total || prev.overdueTotal,
        overdueTotalPages: Math.max(1, Math.ceil((resp.total || prev.overdueTotal) / (state.overdueLimit || 20))),
        overdueLoading: false,
      };
    });
  } catch (e) {
    debugWarn('Error changing overdue page:', e.message);
    debugWarn('Error changing overdue page (full details):', e);
    setState(prev => ({ ...prev, overdueLoading: false }));
  }
};

// Render pagination controls for overdue with numbered pages and ellipses
function renderOverduePaginationControls() {
  const total = state.overdueTotalPages || 1;
  const current = state.overdueDisplayedPage || 1;
  if (total <= 1) return null;

  const pages = [];
  const addPage = (p) => pages.push(p);
  const addEllipsis = (key) => pages.push(`ellipsis-${key}`);

  addPage(1);
  if (current > 3) addEllipsis('left');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let p = start; p <= end; p++) addPage(p);
  if (current < total - 2) addEllipsis('right');
  if (total > 1) addPage(total);

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div>
        Showing {state.overdueBills.length} of {state.overdueTotal} overdue bills
      </div>
      <div className="d-flex align-items-center">
        <Button
          variant="outline-secondary"
          size="sm"
          className="me-2"
          disabled={current <= 1 || state.overdueLoading}
          onClick={() => handleOverduePageChange(current - 1)}
        >
          {'<'}
        </Button>
        {pages.map((p, idx) => (
          typeof p === 'string' ? (
            <span key={p + idx} className="mx-1">…</span>
          ) : (
            <Button
              key={p}
              variant={p === current ? 'primary' : 'outline-primary'}
              size="sm"
              className="mx-1"
              disabled={state.overdueLoading}
              onClick={() => handleOverduePageChange(p)}
            >
              {p}
            </Button>
          )
        ))}
        <Button
          variant="outline-secondary"
          size="sm"
          className="ms-2"
          disabled={current >= total || state.overdueLoading}
          onClick={() => handleOverduePageChange(current + 1)}
        >
          {'>'}
        </Button>
      </div>
    </div>
  );
}

  // Add a pagination controls component
  const renderPaginationControls = () => {
    if (activeTab === "#future") {
      return (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {state.filteredBills.length} of {futureBillsTotal} bills
          </div>
          {futureBillsHasMore && (
            <Button 
              variant="outline-primary" 
              onClick={loadMoreFutureBills}
            >
              Load More
            </Button>
          )}
        </div>
      );
    } else if (activeTab === "#recent") {
      return (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {state.filteredBills.length} of {recentBillsTotal} bills
          </div>
          {recentBillsHasMore && (
            <Button 
              variant="outline-primary" 
              onClick={loadMoreRecentBills}
            >
              Load More
            </Button>
          )}
        </div>
      );
    }
    
    return null;
  };

  // Main rendering logic
  const phoneGraphElement =
    !showAllBills && state.bankAccountId ? (
      <BalanceGraph
        data={
          graphDataWindow.data.length > 0
            ? graphDataWindow.data
            : generateGraphData(
                state.bills,
                state.pays,
                state.overdueBills,
                state.currentBalance,
                graphDataWindow.start,
                graphDataWindow.end
              )
        }
        onPrevWeek={() => setCurrentWeekIndex((prev) => Math.max(prev - 1, 0))}
        onNextWeek={() => setCurrentWeekIndex((prev) => prev + 1)}
        fortnightStartDate={currentWeekStart}
      />
    ) : null;

  return (
    <div>
      {isPhone ? (
        <PhoneBillsView
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          state={state}
          showAllBills={showAllBills}
          trackingPeriod={trackingPeriod}
          setTrackingPeriod={setTrackingPeriod}
          setShowUpdateBalance={setShowUpdateBalance}
          onAddBill={handleAddBillClick}
          onUpdatePaidDate={handleUpdatePaidDate}
          onDelete={handleDelete}
          graphElement={phoneGraphElement}
          formattedStartNextWeek={formattedStartNextWeek}
          formattedRangeSecondWeek={formattedRangeSecondWeek}
          currentWeekStart={currentWeekStart}
          currentWeekEnd={currentWeekEnd}
          renderOverduePaginationControls={renderOverduePaginationControls}
          renderPaginationControls={renderPaginationControls}
        />
      ) : (
        <>
      {/* Bank Account Navigation - show on all bills pages */}
      <BankAccountNavigation 
        bankAccounts={state.bankAccounts}
        currentBankAccountId={state.bankAccountId}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      
      <div className="bills-header-container">
        <div className="bills-nav-header without-title">
          <div className="d-flex justify-content-between align-items-center">
            <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} style={{ width: "90%" }} className="desktop-tabs">
              <Nav.Item>
                <Nav.Link eventKey="#fortnight">This Fortnight</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="#future">Future Bills</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="#recent">Recently Paid</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="#search">Search Bills</Nav.Link>
              </Nav.Item>
            </Nav>
            <div className="me-3 d-flex align-items-center">
              <Button
                size="sm"
                className="me-2 bill-add-btn"
                onClick={handleAddBillClick}
                style={{ whiteSpace: 'nowrap', minWidth: '70px' }}
              >+ Bill</Button>
            </div>
          </div>
        </div>
      </div>
      
      <Card style={{ position: 'relative', zIndex: 1, marginTop: '-0.5rem' }}>
        <Card.Body>
          {activeTab === "#fortnight" && (
            <>
            {/* Only show Cash Flow section for filtered view */}
            {!showAllBills && state.bankAccountId && (
              <Card.Body>
                <h2 className="section-heading">Cash Flow</h2>
                <BalanceGraph
                  data={graphDataWindow.data.length > 0 ? graphDataWindow.data : generateGraphData(
                    state.bills,
                    state.pays, // This includes ALL pays for the current bank account
                    state.overdueBills,
                    state.currentBalance,
                    graphDataWindow.start,
                    graphDataWindow.end
                  )}
                  onPrevWeek={() => setCurrentWeekIndex(prev => Math.max(prev - 1, 0))}
                  onNextWeek={() => setCurrentWeekIndex(prev => prev + 1)}
                  fortnightStartDate={currentWeekStart}
                />
              </Card.Body>
            )}

            {/* Only show Tracking section for filtered view */}
            {!showAllBills && state.bankAccountId ? (
              <Card.Body className="mt-4">
                <div className="section-heading-container">
                  <h2 className="section-heading-inline">Tracking</h2>
                </div>
                {/* Period switcher */}
                <PeriodSwitcher 
                  selectedPeriod={trackingPeriod}
                  onPeriodChange={setTrackingPeriod}
                />
                {/* Period subtitle with dates */}
                {(() => {
                  const periodInfo = calculatePeriodDates(trackingPeriod, state.fortnightDates);
                  return (
                    <h4 className="mb-3 text-muted">
                      {periodInfo.label} ({formatShortDateRange(periodInfo.start, periodInfo.end)})
                    </h4>
                  );
                })()}
                <EquationCards 
                  trackingPeriod={trackingPeriod}
                  state={state}
                  setShowUpdateBalance={setShowUpdateBalance}
                />
                {/* OLD TOTAL CARDS - REPLACED BY EquationCards COMPONENT
                <Row className="d-flex" style={{ display: 'none' }}>
                  <div className="col-md-3">
                    {(() => {
                      // Calculate outgoing amounts based on selected period
                      // Restore original working logic but adapt for new periods
                      let outgoingAmount = 0;
                      
                      debugLog('🔍 DEBUG OUTGOING CALCULATION START:', {
                        trackingPeriod,
                        periodType: typeof trackingPeriod
                      });
                      
                      if (trackingPeriod === 'current-week') {
                        // Original current week logic: use state.totals.totalThisWeek (includes overdue)
                        outgoingAmount = parseFloat(state.totals.totalThisWeek || 0);
                        debugLog('🔍 DEBUG CURRENT WEEK OUTGOING:', {
                          trackingPeriod,
                          totalThisWeek: state.totals.totalThisWeek,
                          outgoingAmount,
                          allTotals: state.totals
                        });
                      } else if (trackingPeriod === 'current-fortnight') {
                        // Current Fortnight: Always includes BOTH weeks of the actual fortnight (week 1 + week 2)
                        // Filter directly from state.bills using fortnight dates to ensure we get the correct weeks
                        
                        const firstWeekStart = new Date(state.fortnightDates.firstWeekStart);
                        const firstWeekEnd = new Date(state.fortnightDates.firstWeekEnd);
                        const secondWeekStart = new Date(state.fortnightDates.secondWeekStart);
                        const secondWeekEnd = new Date(state.fortnightDates.secondWeekEnd);
                        
                        // Get first week unpaid bills (exclude pays)
                        const firstWeekBills = (state.bills || []).filter(b => {
                          const billDate = new Date(b.due_date);
                          return billDate >= firstWeekStart && billDate <= firstWeekEnd && !b.paid && b.description !== 'incoming pay';
                        });
                        const firstWeekTotal = firstWeekBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                        
                        // Get second week unpaid bills (exclude pays) 
                        const secondWeekBills = (state.bills || []).filter(b => {
                          const billDate = new Date(b.due_date);
                          return billDate >= secondWeekStart && billDate <= secondWeekEnd && !b.paid && b.description !== 'incoming pay';
                        });
                        const secondWeekTotal = secondWeekBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                        
                        // Prior overdue unpaid (already filtered to this bank account upstream)
                        const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
                        const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                        
                        outgoingAmount = firstWeekTotal + secondWeekTotal + overdueTotal;
                        
                        debugLog('🔍 DEBUG CURRENT FORTNIGHT OUTGOING:', {
                          trackingPeriod,
                          firstWeekTotal,
                          secondWeekTotal,
                          overdueTotal,
                          outgoingAmount,
                          firstWeekBillsCount: firstWeekBills.length,
                          secondWeekBillsCount: secondWeekBills.length,
                          overdueBillsCount: (state.overdueBills || []).length,
                          fortnightDates: {
                            firstWeek: `${firstWeekStart.toISOString().split('T')[0]} to ${firstWeekEnd.toISOString().split('T')[0]}`,
                            secondWeek: `${secondWeekStart.toISOString().split('T')[0]} to ${secondWeekEnd.toISOString().split('T')[0]}`
                          }
                        });
                      } else if (trackingPeriod === 'two-weeks') {
                        // Two weeks = always current week + next week bills (from the bill tables)
                        const unpaidThisWeek = (state.currentWeekBills || []).filter(b => !b.paid && b.description !== 'incoming pay');
                        const unpaidThisWeekTotal = unpaidThisWeek.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                        const unpaidNextWeek = (state.nextWeekBills || []).filter(b => !b.paid && b.description !== 'incoming pay');
                        const unpaidNextWeekTotal = unpaidNextWeek.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                        // Include overdue bills as well
                        const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
                        const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                        outgoingAmount = unpaidThisWeekTotal + unpaidNextWeekTotal + overdueTotal;
                      } else {
                        // For multi-week periods (three weeks, four weeks)
                        // Get the correct date range for the period
                        const periodInfo = calculatePeriodDates(trackingPeriod, state.fortnightDates);
                        const periodStart = new Date(periodInfo.start);
                        const periodEnd = new Date(periodInfo.end);
                        
                        // Filter bills within the period date range
                        const periodBills = (state.bills || []).filter(b => {
                          const billDate = new Date(b.due_date);
                          return billDate >= periodStart && billDate <= periodEnd && !b.paid && b.description !== 'incoming pay';
                        });
                        const periodTotal = periodBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                        
                        // Include overdue bills as well
                        const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
                        const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                        
                        outgoingAmount = periodTotal + overdueTotal;
                        
                        debugLog(`🔍 DEBUG ${trackingPeriod.toUpperCase()} OUTGOING:`, {
                          trackingPeriod,
                          periodTotal,
                          overdueTotal,
                          outgoingAmount,
                          periodBillsCount: periodBills.length,
                          overdueBillsCount: (state.overdueBills || []).length,
                          periodDateRange: `${periodStart.toISOString().split('T')[0]} to ${periodEnd.toISOString().split('T')[0]}`
                        });
                      }
                      
                      return (
                    <TotalCard
                          title="Outgoing"
                          amount={outgoingAmount.toFixed(2)}
                      highlight={'highlight'}
                    />
                      );
                    })()}
                  </div>
                  <div className="col-md-3">
                    <Button
                      variant="link"
                      onClick={() => setShowUpdateBalance(true)}
                      style={{
                        padding: 0,
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#333',
                        width: '100%',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.transform = 'scale(1.04)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <TotalCard
                        title="Current Balance"
                        amount={state.currentBalance}
                      />
                    </Button>
                  </div>
                                    <div className="col-md-3">
                    {(() => {
                      // Calculate incoming amounts based on selected period
                      // Restore original working logic but adapt for new periods
                      let incomingTotal = 0;
                      
                      if (trackingPeriod === 'current-week') {
                        // Original current week logic: simple totalUnpaidPaysThisWeek
                        incomingTotal = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0);
                        debugLog('🔍 DEBUG CURRENT WEEK INCOMING:', {
                          trackingPeriod,
                          totalUnpaidPaysThisWeek: state.transferTotals.totalUnpaidPaysThisWeek,
                          incomingTotal,
                          allTransferTotals: state.transferTotals
                        });
                      } else if (trackingPeriod === 'current-fortnight') {
                        // Original fortnight logic: include fortnight transfers + overdue transfers for actual fortnight
                        const fortnightTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
                        
                        // Add previously unpaid transfers (overdue transfers)
                        const overdueTransfers = (state.pays || []).filter(pay => {
                          if (!pay || pay.transferMade || pay.paid) return false;
                          const payDate = new Date(pay.due_date || pay.next_pay);
                          const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
                          return payDate <= currentWeekStart;
                        });
                        const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
                        
                        incomingTotal = fortnightTransfers + overdueTransfersTotal;
                      } else if (trackingPeriod === 'two-weeks') {
                        // Two weeks = always current week + next week transfers (regardless of fortnight position)
                        const twoWeeksTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
                        
                        // Add overdue transfers
                        const overdueTransfers = (state.pays || []).filter(pay => {
                          if (!pay || pay.transferMade || pay.paid) return false;
                          const payDate = new Date(pay.due_date || pay.next_pay);
                          const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
                          return payDate <= currentWeekStart;
                        });
                        const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
                        
                        incomingTotal = twoWeeksTransfers + overdueTransfersTotal;
                      } else {
                        // For multi-week periods (three weeks, four weeks)
                        // Use fortnight calculation for now (could be enhanced with more detailed period logic)
                        const fortnightTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
                        
                        const overdueTransfers = (state.pays || []).filter(pay => {
                          if (!pay || pay.transferMade || pay.paid) return false;
                          const payDate = new Date(pay.due_date || pay.next_pay);
                          const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
                          return payDate <= currentWeekStart;
                        });
                        const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
                        
                        incomingTotal = fortnightTransfers + overdueTransfersTotal;
                      }
                      
                      return (
                        <TotalCard
                          title="Incoming"
                          amount={incomingTotal.toFixed(2)}
                        />
                      );
                    })()}
                  </div>
                  <div className="col-md-3">
                     {(() => {
                       if (trackingPeriod === 'current-week') {
                         // For current week, keep the original "Difference" card
                         const baseOwed = parseFloat(state.totals.totalThisWeek || 0);
                         const transfersForDisplay = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0);
                         const difference = (parseFloat(state.currentBalance || 0) + transfersForDisplay - baseOwed).toFixed(2);
                         
                         return (
                           <TotalCard
                             title="Difference"
                             amount={difference}
                             highlight={'secondary'}
                           />
                         );
                       } else {
                         // For all other periods, show the new "Closing Balance" card
                                                  // Get outgoing amount (same calculation as Outgoing card)
                         let outgoingAmount = 0;
                         if (trackingPeriod === 'current-fortnight') {
                           // Current Fortnight: Always includes BOTH weeks of the actual fortnight (week 1 + week 2)
                           const firstWeekStart = new Date(state.fortnightDates.firstWeekStart);
                           const firstWeekEnd = new Date(state.fortnightDates.firstWeekEnd);
                           const secondWeekStart = new Date(state.fortnightDates.secondWeekStart);
                           const secondWeekEnd = new Date(state.fortnightDates.secondWeekEnd);
                           
                           // Get first week unpaid bills (exclude pays)
                           const firstWeekBills = (state.bills || []).filter(b => {
                             const billDate = new Date(b.due_date);
                             return billDate >= firstWeekStart && billDate <= firstWeekEnd && !b.paid && b.description !== 'incoming pay';
                           });
                           const firstWeekTotal = firstWeekBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                           
                           // Get second week unpaid bills (exclude pays) 
                           const secondWeekBills = (state.bills || []).filter(b => {
                             const billDate = new Date(b.due_date);
                             return billDate >= secondWeekStart && billDate <= secondWeekEnd && !b.paid && b.description !== 'incoming pay';
                           });
                           const secondWeekTotal = secondWeekBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                           
                           const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
                           const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                           outgoingAmount = firstWeekTotal + secondWeekTotal + overdueTotal;
                         } else if (trackingPeriod === 'two-weeks') {
                           const unpaidThisWeek = (state.currentWeekBills || []).filter(b => !b.paid && b.description !== 'incoming pay');
                           const unpaidThisWeekTotal = unpaidThisWeek.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                           const unpaidNextWeek = (state.nextWeekBills || []).filter(b => !b.paid && b.description !== 'incoming pay');
                           const unpaidNextWeekTotal = unpaidNextWeek.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                           const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
                           const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                           outgoingAmount = unpaidThisWeekTotal + unpaidNextWeekTotal + overdueTotal;
                         } else {
                           // For multi-week periods (three weeks, four weeks)
                           const periodInfo = calculatePeriodDates(trackingPeriod, state.fortnightDates);
                           const periodStart = new Date(periodInfo.start);
                           const periodEnd = new Date(periodInfo.end);
                           
                           // Filter bills within the period date range
                           const periodBills = (state.bills || []).filter(b => {
                             const billDate = new Date(b.due_date);
                             return billDate >= periodStart && billDate <= periodEnd && !b.paid && b.description !== 'incoming pay';
                           });
                           const periodTotal = periodBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                           
                           const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
                           const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                           outgoingAmount = periodTotal + overdueTotal;
                         }
                         
                         // Get incoming amount (same calculation as Incoming card)
                         let incomingAmount = 0;
                         if (trackingPeriod === 'current-fortnight') {
                           const fortnightTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
                           const overdueTransfers = (state.pays || []).filter(pay => {
                             if (!pay || pay.transferMade || pay.paid) return false;
                             const payDate = new Date(pay.due_date || pay.next_pay);
                             const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
                             return payDate <= currentWeekStart;
                           });
                           const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
                           incomingAmount = fortnightTransfers + overdueTransfersTotal;
                         } else if (trackingPeriod === 'two-weeks') {
                           const twoWeeksTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
                           const overdueTransfers = (state.pays || []).filter(pay => {
                             if (!pay || pay.transferMade || pay.paid) return false;
                             const payDate = new Date(pay.due_date || pay.next_pay);
                             const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
                             return payDate <= currentWeekStart;
                           });
                           const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
                           incomingAmount = twoWeeksTransfers + overdueTransfersTotal;
                         } else {
                           const fortnightTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
                           const overdueTransfers = (state.pays || []).filter(pay => {
                             if (!pay || pay.transferMade || pay.paid) return false;
                             const payDate = new Date(pay.due_date || pay.next_pay);
                             const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
                             return payDate <= currentWeekStart;
                           });
                           const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
                           incomingAmount = fortnightTransfers + overdueTransfersTotal;
                         }
                         
                         const closingBalance = parseFloat(state.currentBalance || 0) + incomingAmount - outgoingAmount;
                         const isNegative = closingBalance < 0;
                         
                       return (
                    <TotalCard
                             title="Closing Balance"
                             amount={closingBalance.toFixed(2)}
                             highlight={isNegative ? null : 'highlight'}
                             isNegative={isNegative}
                           />
                         );
                       }
                     })()}
                  </div>
                </Row>
                END OF OLD TOTAL CARDS */}
              </Card.Body>
            ) : null}

            <Card.Body className="bills-fortnight-body">
              {showAllBills && (
                <div className="section-heading-container">
                  <h4 className="mb-0">({formatShortDateRange(currentWeekStart, nextWeekEnd)})</h4>
                </div>
              )}

              {state.currentWeekBills.length === 0 && state.nextWeekBills.length === 0 && !showAllBills && (
                <div className="alert alert-info" style={{ textAlign: "center", marginBottom: "20px" }}>
                  <h5 style={{ fontWeight: "bold" }}>
                    No bills for this account
                  </h5>
                  <p>
                    This bank account doesn't have any associated bills yet. You can still track your 
                    cash flow and add bills to this account if needed.
                  </p>
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={handleAddBillClick}
                  >
                    Add a Bill to This Account
                  </Button>
                </div>
              )}
              
              {(() => {
                const shouldShow = showAllBills || !urlBankAccountId || state.currentWeekBills.length > 0 || state.nextWeekBills.length > 0;
                debugLog('🎯 RENDER DECISION:', {
                  shouldShow,
                  showAllBills,
                  urlBankAccountId,
                  currentWeekBillsLength: state.currentWeekBills.length,
                  nextWeekBillsLength: state.nextWeekBills.length,
                  activeTab
                });
                return shouldShow;
              })() && (
                <>
                  <div style={{ textAlign: "center", marginBottom: "20px" }}>
                    <h2 style={{ fontSize: "24px", fontWeight: "bold", borderBottom: "2px solid #A0C2BB", display: "inline-block", paddingBottom: "10px" }}>
                      <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: "10px" }} />
                      Current Bills
                    </h2>
                    <br />
                    <span style={{ fontSize: "18px", color: "#555" }}>Due Before {formattedStartNextWeek}</span>
                  </div>
                  
                  {/* Display prior unpaid bills (overdue) with pagination */}
                  {state.overdueBills && state.overdueBills.length > 0 ? (
                      <>
                        <CollapsibleBillGroup
                          title="Overdue"
                          variant="warning"
                          icon={faExclamationTriangle}
                          tooltip="These bills were due before the current week and remain unpaid. Showing the most recent overdue items — use the Search tab for older overdue bills."
                        >
                          <BillsTable
                            bills={state.overdueBills}
                            onUpdatePaidDate={handleUpdatePaidDate}
                            bankAccountId={state.bankAccountId}
                            selectedBills={selectedBills}
                            onSelect={handleBillSelect}
                            onDelete={handleDelete}
                            showAllBills={showAllBills}
                            bankAccounts={state.bankAccounts}
                          />
                          {renderOverduePaginationControls()}
                        </CollapsibleBillGroup>
                        <hr className="bills-section-divider" />
                        <BillGroupLabel
                          title="This week"
                          variant="info"
                          icon={faCalendarWeek}
                          tooltip={`Bills due in the current week (${formatShortDateRange(currentWeekStart, currentWeekEnd)})`}
                        />
                      </>
                    ) : null}
                  
                  <BillsTable
                    bills={(() => {
                      const currentBills = state.currentWeekBills.filter(bill => !(new Date(bill.due_date) < currentWeekStart && !bill.paid));
                      debugLog('🎯 CURRENT WEEK BILLS FOR TABLE:', {
                        totalCurrentWeekBills: state.currentWeekBills.length,
                        filteredCurrentBills: currentBills.length,
                        bills: currentBills
                      });
                      return currentBills;
                    })()}
                    onUpdatePaidDate={handleUpdatePaidDate}
                    bankAccountId={state.bankAccountId}
                    selectedBills={selectedBills}
                    onSelect={handleBillSelect}
                    onDelete={handleDelete}
                    showAllBills={showAllBills}
                    bankAccounts={state.bankAccounts}
                  />

                  {/* Incorporate PaidBillsGroup for current week paid bills */}
                  <PaidBillsGroup
                    groupTitle="Paid This Week"
                    bills={state.currentWeekPaidBills}
                    onUpdatePaidDate={handleUpdatePaidDate}
                    bankAccountId={state.bankAccountId}
                    onDelete={handleDelete}
                    showAllBills={showAllBills}
                    bankAccounts={state.bankAccounts}
                  />

                  <div className="bills-next-week-header" style={{ textAlign: "center", marginBottom: "20px" }}>
                    <h2 style={{ fontSize: "24px", fontWeight: "bold", borderBottom: "2px solid #A0C2BB", display: "inline-block", paddingBottom: "10px" }}>
                      <FontAwesomeIcon icon={faBinoculars} style={{ marginRight: "10px" }} />
                      Next Week
                    </h2>
                    <br />
                    <span style={{ fontSize: "18px", color: "#555" }}>due within {formattedRangeSecondWeek}</span>
                  </div>
                  <BillsTable
                    bills={(() => {
                      debugLog('🎯 NEXT WEEK BILLS FOR TABLE:', {
                        nextWeekBills: state.nextWeekBills.length,
                        bills: state.nextWeekBills
                      });
                      return state.nextWeekBills;
                    })()}
                    onUpdatePaidDate={handleUpdatePaidDate}
                    bankAccountId={state.bankAccountId}
                    selectedBills={selectedBills}
                    onSelect={handleBillSelect}
                    onDelete={handleDelete}
                    showAllBills={showAllBills}
                    bankAccounts={state.bankAccounts}
                  />

                  {/* Incorporate PaidBillsGroup for next week paid bills */}
                  <PaidBillsGroup
                    groupTitle="Next Week Bills Already Paid"
                    bills={state.nextWeekPaidBills}
                    onUpdatePaidDate={handleUpdatePaidDate}
                    bankAccountId={state.bankAccountId}
                    onDelete={handleDelete}
                    showAllBills={showAllBills}
                    bankAccounts={state.bankAccounts}
                  />
                </>
              )}
            </Card.Body>

            </>
          )}
          {activeTab === "#future" && (
            <>
              <Card.Body className="mt-4">
                {!showAllBills && state.bankAccountId ? (
                  <div className="section-heading-container">
                    <h2 className="section-heading-inline">Future Bills</h2>
                    <h4>Scheduled After {formattedStartNextFort}</h4>
                  </div>
                ) : (
                  <div className="text-center mb-4">
                    <h2 style={{ fontSize: "24px", fontWeight: "bold", borderBottom: "2px solid #A0C2BB", display: "inline-block", paddingBottom: "10px" }}>
                      <FontAwesomeIcon icon={faGolfBallTee} style={{ marginRight: "10px" }} />
                      Bills AFTER current fortnight
                    </h2>
                    <br />
                    <span style={{ fontSize: "18px", color: "#555" }}>due in the 30 days after {formattedStartNextFort}</span>
                  </div>
                )}
                
                {state.filteredBills.length > 0 ? (
                  <>
                    <BillsTable
                      bills={state.filteredBills}
                      onUpdatePaidDate={handleUpdatePaidDate}
                      selectedBills={selectedBills}
                      onSelect={handleBillSelect}
                      bankAccountId={state.bankAccountId}
                      onDelete={handleDelete}
                      showAllBills={showAllBills}
                      bankAccounts={state.bankAccounts}
                    />
                    {renderPaginationControls()}
                  </>
                ) : (
                  <div className="text-center p-5">
                    <h4 style={{ color: "#6c757d" }}>No future bills found</h4>
                    <p>All scheduled bills are within the current fortnight.</p>
                  </div>
                )}
              </Card.Body>
            </>
          )}
          {activeTab === "#recent" && (
            <>
              <Card.Body className="mt-4">
                {!showAllBills && state.bankAccountId ? (
                  <div className="section-heading-container">
                    <h2 className="section-heading-inline">Recently Paid Bills</h2>
                    <h4>Paid in the Last Month</h4>
                  </div>
                ) : (
                  <div className="text-center mb-4">
                    <h2 style={{ fontSize: "24px", fontWeight: "bold", borderBottom: "2px solid #A0C2BB", display: "inline-block", paddingBottom: "10px" }}>
                      <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: "10px" }} />
                      Recently Paid Bills
                    </h2>
                    <br />
                    <span style={{ fontSize: "18px", color: "#555" }}>paid in the last month</span>
                  </div>
                )}
                
                {state.filteredBills.length > 0 ? (
                  <>
                    <BillsTable
                      bills={state.filteredBills}
                      onUpdatePaidDate={handleUpdatePaidDate}
                      selectedBills={selectedBills}
                      onSelect={handleBillSelect}
                      bankAccountId={state.bankAccountId}
                      showAllBills={showAllBills}
                      onDelete={handleDelete}
                      bankAccounts={state.bankAccounts}
                    />
                    {renderPaginationControls()}
                  </>
                ) : (
                  <div className="text-center p-5">
                    <h4 style={{ color: "#6c757d" }}>No recently paid bills found</h4>
                    <p>Try expanding the date range or checking different bank accounts.</p>
                  </div>
                )}
              </Card.Body>
            </>
          )}

          {activeTab === "#search" && (
            <BillSearch 
              bankAccountId={state.bankAccountId}
              showAllBills={showAllBills}
            />
          )}
        </Card.Body>
      </Card>
        </>
      )}

      <Modal show={showUpdateBalance} onHide={() => setShowUpdateBalance(false)} dialogClassName="mobile-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            Update Balance for{' '}
            {state.bankAccounts?.find((acc) => acc.id === state.bankAccountId)?.nickname ||
              'Bank Account'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UpdateBalance onUpdate={handleBalanceUpdate} bankAccountId={state.bankAccountId} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateBalance(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for Adding Bill opened by the '+ Bill' button */}
      <Modal
        show={showAddBillModal}
        onHide={() => setShowAddBillModal(false)}
        size="lg"
        dialogClassName="mobile-responsive-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Bill</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddBillForm 
            defaultBankAccountId={urlBankAccountId}
            onSuccess={() => {
              setShowAddBillModal(false);
              fetchInitialData(); // Refresh data after adding a bill
            }} 
          />
        </Modal.Body>
      </Modal>

      {selectedBills.length > 0 && !isPhone && (
        <BillSelectionPortal
          selectedBills={selectedBills}
          totalAmount={selectedBills.reduce((sum, bill) => sum + parseFloat(bill.amount), 0)}
          weeklyBudget={state.totals.totalThisWeek}
          onClearAll={() => setSelectedBills([])}
          onMinimize={() => {}}
        />
      )}
    </div>
  );
};

export default Bills;
